// ============================================================================
// STORE.REFERRALS.JS - Firestore Persistence Layer for Referrals
// 
// This module handles all Firestore reads/writes for:
//   - ReferralDoc:    tenants/{tenantId}/referrals/{referralId}
//   - MetricEventDoc: tenants/{tenantId}/metrics/{eventId}
//   - AuditLogDoc:    tenants/{tenantId}/referrals/{referralId}/auditLogs/{runId}
// 
// RULE: All writes go through this module. No direct Firestore writes elsewhere.
// ============================================================================

import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter as startAfterDoc,
  Timestamp,
  serverTimestamp,
  writeBatch,
  runTransaction,
  increment as firestoreIncrement,
  arrayUnion,
} from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { PATHS } from './firestorePaths.js';
import { assertSafeNeverPhiDoc, redactSensitiveForLog } from './sensitiveDataPolicy.js';
import { buildEhrCandidateProvenance } from './ehrCandidateProvenance.js';
import { ensureWorkflowOnSubmit, updateWorkflowOnRerun, WORKFLOW_STATUS } from './store.workflow.js';

// Sensitive-data boundary: the referral root is a Never-PHI zone. Clinical
// free-text subtrees (answers, override/outcome reasons, escalation/visit text)
// are governed by pack authoring + phiScan.js — NOT this structural guard — so
// they are treated as opaque (their KEY names are still checked).
const REFERRAL_ROOT_OPAQUE_KEYS = ['answers', 'outcome', 'override', 'reasonForEscalation', 'visitPurpose', 'telehealthContext', 'patientMessages'];

import {
  buildReferralDoc,
  buildMetricEvent,
  buildAuditLog,
  buildImpactSettings,
  buildSubmissionLock,
  buildRunSession,
  buildDailyRollup,
  buildDailyRollupIncrements,
  buildPhiPolicy,
  validateReferralDoc,
  validateMetricEvent,
  validateAuditLog,
  validateImpactSettings,
  validateSubmissionLock,
  validateRunSession,
  validateDailyRollup,
  validatePhiPolicy,
  generateReferralId,
  generateMetricId,
  generateRunSessionId,
  generateDateId,
  toTimestamp,
  normalizeOutcomeStatus,
  METRIC_ALLOWED_FIELDS,
  DEFAULT_IMPACT_SETTINGS,
  // BLOCK 13: PHI validation
  sanitizeAnswersForStorage,
  assertAnswersStorageSafe,
  DEFAULT_PHI_POLICY,
} from './schemas.js';

// Import generateId from engine.js for error ID generation
import { generateId } from './engine.js';

// S1 — Never-PHI write hardening: redact high-confidence PHI values from the
// audit-log trace (the trace stringifies answer values — pathway-engine.js).
import { redactBlockingPhiDeep } from '../utils/phiScan.js';

// ============================================================================
// DEV LOGGING
// ----------------------------------------------------------------------------
// Diagnostic logs are gated to development builds only. In production these are
// no-ops so we never leak Firestore path structure / submission internals to the
// browser console (information disclosure on a HIPAA-adjacent surface).
//
// IMPORTANT: this gates console.log diagnostics ONLY. Genuine console.error /
// console.warn failure reporting is intentionally left ungated and must keep
// surfacing (fail-fast — never silently swallow errors).
//
// `import.meta.env?.DEV` is used (optional chaining) so this module stays safe
// when imported under plain Node (e.g. *.verify.js scripts), where
// `import.meta.env` is undefined; under Vite it is statically known.
// ============================================================================

const devLog = (...args) => {
  if (import.meta.env?.DEV) console.log(...args);
};

// ============================================================================
// FIREBASE INSTANCE
// ============================================================================

let db = null;

const getDb = () => {
  if (!db) {
    db = getFirestore(getApp());
  }
  return db;
};

// ============================================================================
// BLOCK 9: RELIABILITY HELPERS (Retry + Idempotency)
// ============================================================================

/**
 * Generate deterministic submission key for idempotency
 * @param {string} tenantId
 * @param {string} referralId
 * @param {string} createdByUid
 * @returns {string} submission key
 */
const generateSubmissionKey = (tenantId, referralId, createdByUid) => {
  // Simple deterministic key: tenant + referral + user
  // This ensures same user submitting same referral gets same key
  return `${tenantId}_${referralId}_${createdByUid}`;
};

/**
 * Sleep helper for exponential backoff
 * @param {number} ms - milliseconds to wait
 * @returns {Promise<void>}
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Check if a Firestore error is retryable
 * @param {Error} error
 * @returns {boolean}
 */
const isRetryableError = (error) => {
  if (!error || !error.code) return false;

  const retryableCodes = [
    'unavailable',
    'deadline-exceeded',
    'resource-exhausted',
    'aborted',
    'internal',
    'cancelled',
  ];

  return retryableCodes.includes(error.code);
};

/**
 * Execute a function with exponential backoff retry
 * @param {Function} fn - Async function to execute
 * @param {Object} options - Retry options
 * @returns {Promise<any>}
 */
const retryWithBackoff = async (fn, options = {}) => {
  const {
    maxAttempts = 3,
    baseDelayMs = 250,
    maxDelayMs = 2000,
    jitterMs = 100,
  } = options;

  let lastError = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      // Don't retry on non-retryable errors
      if (!isRetryableError(error)) {
        console.error(`[retryWithBackoff] Non-retryable error on attempt ${attempt}:`, error);
        throw error;
      }

      // Don't retry on last attempt
      if (attempt === maxAttempts) {
        console.error(`[retryWithBackoff] Max attempts (${maxAttempts}) reached:`, error);
        throw error;
      }

      // Calculate backoff delay with jitter
      const exponentialDelay = Math.min(baseDelayMs * Math.pow(2, attempt - 1), maxDelayMs);
      const jitter = Math.random() * jitterMs;
      const delayMs = exponentialDelay + jitter;

      console.warn(`[retryWithBackoff] Retryable error on attempt ${attempt}/${maxAttempts}, retrying in ${Math.round(delayMs)}ms:`, error.code);
      await sleep(delayMs);
    }
  }

  throw lastError;
};

// ============================================================================
// DAILY ROLLUP HELPER (BLOCK 12) - DISABLED (Now handled by Cloud Function)
// ============================================================================

/**
 * INCREMENT DAILY ROLLUP - CLIENT-SIDE VERSION (DISABLED)
 *
 * ⚠️ DEPRECATED: Client-side rollup writes have been moved to Cloud Functions
 * to ensure reliability and prevent double-counting / missed increments.
 *
 * This function is kept for reference and backfill tools but is NO LONGER
 * called from submitReferral or rerunGate.
 *
 * Daily rollups are now updated server-side by the onMetricEventCreate
 * Cloud Function trigger, with idempotency guarantees via marker docs.
 *
 * See: functions/index.js -> onMetricEventCreate_updateDailyRollup
 *
 * Original documentation:
 * Increment daily rollup counters for a metric event (BLOCK 12)
 *
 * Uses Firestore FieldValue.increment() to atomically update counters
 * Creates rollup doc if it doesn't exist (via setDoc with merge)
 *
 * FIRESTORE WRITE:
 *   - tenants/{tenantId}/rollups/daily/{dateId} (set with merge)
 *
 * @param {Object} batch - Firestore WriteBatch (optional; if provided, adds to batch instead of direct write)
 * @param {string} tenantId - Tenant ID
 * @param {Object} metricEvent - Metric event doc
 * @param {number} minutesSavedPerPreventedReferral - From impact settings (default 5)
 * @returns {Promise<void>}
 */
const incrementDailyRollup_DISABLED_CLIENT_SIDE = async (batch, tenantId, metricEvent, minutesSavedPerPreventedReferral = 5) => {
  console.warn('[incrementDailyRollup] ⚠️ CLIENT-SIDE ROLLUP DISABLED - rollups are now handled by Cloud Functions');
  console.warn('[incrementDailyRollup] This function is no-op. Daily rollups will be updated server-side.');
  // Do nothing - rollups are now server-side only
  return;

  // Original implementation kept for reference (not executed):
  /*
  if (!metricEvent || !metricEvent.savedAt) {
    console.warn('[incrementDailyRollup] Missing metricEvent or savedAt, skipping rollup');
    return;
  }

  const db = getDb();
  const dateId = generateDateId(metricEvent.savedAt);
  // Path: tenants/{tenantId}/rollups/daily/dates/{dateId} (6 segments for valid Firestore doc ref)
  const rollupRef = doc(db, PATHS.rollupDaily(tenantId, dateId));

  // Build increment object
  const increments = buildDailyRollupIncrements(metricEvent, minutesSavedPerPreventedReferral);

  // Convert increment values to Firestore increment()
  const updateData = {
    tenantId,
    dateId,
    updatedAt: toTimestamp(),
  };

  Object.entries(increments).forEach(([path, value]) => {
    updateData[path] = firestoreIncrement(value);
  });

  if (batch) {
    // Add to batch (for atomic submission with referral/metrics/audit)
    batch.set(rollupRef, updateData, { merge: true });
  } else {
    // Direct write (for standalone increment, e.g., markPdfGenerated)
    try {
      await setDoc(rollupRef, updateData, { merge: true });
      devLog(`[incrementDailyRollup] ✅ Rollup incremented for ${dateId}`);
    } catch (err) {
      // Non-blocking: rollup failure should not block submission
      console.error('[incrementDailyRollup] ❌ Failed to increment rollup (non-blocking):', err);
    }
  }
  */
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Clean a Firestore document snapshot to plain object
 */
const cleanDoc = (docSnap) => {
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() };
};

// ============================================================================
// REFERRAL OPERATIONS
// Path: tenants/{tenantId}/referrals/{referralId}
// ============================================================================

/**
 * Save a referral document to Firestore
 * 
 * @param {string} tenantId
 * @param {Object} referralData - Data from buildReferralDoc or raw params
 * @returns {Promise<{success: boolean, referralId: string, runId: string}>}
 * @throws {Error} If validation fails or write fails
 */
export const saveReferral = async (tenantId, referralData) => {
  const db = getDb();
  
  // Build document if raw params provided
  let referralDoc;
  if (referralData.referralId && referralData.runId && referralData.patient) {
    // Already built
    referralDoc = referralData;
  } else {
    // Build from raw params
    referralDoc = buildReferralDoc({
      ...referralData,
      tenantId,
    });
  }
  
  // Validate
  const validation = validateReferralDoc(referralDoc);
  if (!validation.valid) {
    console.error('[ERROR] saveReferral validation failed:', validation.errors);
    throw new Error(`saveReferral validation failed: ${validation.errors.join(', ')}`);
  }
  
  // Ensure tenantId matches
  referralDoc.tenantId = tenantId;

  // S1 — Never-PHI write hardening: redact high-confidence PHI values from
  // clinical answers before the guard + write (covers callers that hand in a
  // prebuilt referralDoc, not only the Gate submit path).
  if (referralDoc.answers && typeof referralDoc.answers === 'object') {
    referralDoc = {
      ...referralDoc,
      answers: sanitizeAnswersForStorage(referralDoc.answers, null, DEFAULT_PHI_POLICY),
    };
    assertAnswersStorageSafe(referralDoc.answers, 'referral.answers');
  }

  // Sensitive-data boundary (hard-fail before write — no partial state).
  assertSafeNeverPhiDoc(referralDoc, { context: 'referral root', opaqueKeys: REFERRAL_ROOT_OPAQUE_KEYS });

  const referralId = referralDoc.referralId || generateReferralId();
  const referralRef = doc(db, PATHS.referral(tenantId, referralId));

  await setDoc(referralRef, referralDoc);
  
  return {
    success: true,
    referralId,
    runId: referralDoc.runId,
  };
};

// ============================================================================
// createDraftReferral
//
// Creates a minimal "draft" referral doc so the PHI Cloud Functions have a
// referral to anchor to before the full submission batch runs.
//
// The doc is a stub: { tenantId, createdByUid, status: 'draft' }.
// submitReferral() accepts the same referralId and overwrites this doc with
// the full referral data (Firestore rules allow gp_employee to update their
// own draft docs — see firestore.rules match /referrals/{referralId}).
//
// @param {string} tenantId
// @param {string} createdByUid  — Firebase Auth UID of the gp_employee
// @returns {Promise<{ referralId: string }>}
// ============================================================================

export const createDraftReferral = async (tenantId, createdByUid) => {
  const db = getDb();
  const referralId = generateReferralId();
  const referralRef = doc(db, PATHS.referral(tenantId, referralId));
  await setDoc(referralRef, {
    tenantId,
    createdByUid,
    status: 'draft',
    createdAt: serverTimestamp(),
  });
  return { referralId };
};

/**
 * Get a referral by ID
 *
 * @param {string} tenantId
 * @param {string} referralId
 * @returns {Promise<Object|null>}
 */
export const getReferral = async (tenantId, referralId) => {
  const db = getDb();
  const referralRef = doc(db, PATHS.referral(tenantId, referralId));
  const snapshot = await getDoc(referralRef);
  return cleanDoc(snapshot);
};

/**
 * Get referrals for a user (My Submissions)
 * 
 * @param {string} tenantId
 * @param {string} userId
 * @param {Object} options - { pageSize, startAfter, outcomeStatus }
 * @returns {Promise<{rows: Array, lastDoc: any, hasMore: boolean}>}
 */
export const getReferralsByUser = async (tenantId, userId, options = {}) => {
  const db = getDb();
  const { pageSize = 50, startAfter = null, outcomeStatus = null } = options;
  
  const referralsRef = collection(db, PATHS.col.referrals(tenantId));
  
  let constraints = [
    where('createdByUid', '==', userId),
    orderBy('savedAt', 'desc'),
  ];
  
  if (outcomeStatus) {
    constraints.push(where('outcome.status', '==', normalizeOutcomeStatus(outcomeStatus)));
  }
  
  if (startAfter) {
    constraints.push(startAfterDoc(startAfter));
  }
  
  constraints.push(limit(pageSize + 1));
  
  const q = query(referralsRef, ...constraints);
  const snapshot = await getDocs(q);
  
  const rows = [];
  let lastDoc = null;
  
  snapshot.forEach((docSnap, index) => {
    if (index < pageSize) {
      const data = cleanDoc(docSnap);
      if (data) {
        data._docRef = docSnap;
        rows.push(data);
      }
      lastDoc = docSnap;
    }
  });
  
  const hasMore = snapshot.size > pageSize;
  
  return { rows, lastDoc, hasMore };
};

/**
 * Get referrals for a specialty (Specialist view)
 * 
 * @param {string} tenantId
 * @param {string} specialtyId
 * @param {Object} options - { pageSize, startAfter, outcomeStatus }
 * @returns {Promise<{rows: Array, lastDoc: any, hasMore: boolean}>}
 */
export const getReferralsBySpecialty = async (tenantId, specialtyId, options = {}) => {
  const db = getDb();
  const { pageSize = 50, startAfter = null, outcomeStatus = null } = options;
  
  const referralsRef = collection(db, PATHS.col.referrals(tenantId));
  
  let constraints = [
    where('specialtyId', '==', specialtyId),
    orderBy('savedAt', 'desc'),
  ];
  
  if (outcomeStatus) {
    constraints.push(where('outcome.status', '==', normalizeOutcomeStatus(outcomeStatus)));
  }
  
  if (startAfter) {
    constraints.push(startAfterDoc(startAfter));
  }
  
  constraints.push(limit(pageSize + 1));
  
  const q = query(referralsRef, ...constraints);
  const snapshot = await getDocs(q);
  
  const rows = [];
  let lastDoc = null;
  
  snapshot.forEach((docSnap, index) => {
    if (index < pageSize) {
      const data = cleanDoc(docSnap);
      if (data) {
        data._docRef = docSnap;
        rows.push(data);
      }
      lastDoc = docSnap;
    }
  });
  
  const hasMore = snapshot.size > pageSize;
  
  return { rows, lastDoc, hasMore };
};

// ============================================================================
// METRIC EVENT OPERATIONS
// Path: tenants/{tenantId}/metrics/{eventId}
// ============================================================================

/**
 * Save a metric event to Firestore
 * 
 * @param {string} tenantId
 * @param {Object} metricData - Data from buildMetricEvent or raw params
 * @returns {Promise<{success: boolean, eventId: string}>}
 * @throws {Error} If validation fails or write fails
 */
export const saveMetricEventV2 = async (tenantId, metricData) => {
  const db = getDb();
  
  // Build document if raw params provided
  let metricDoc;
  if (metricData.eventId && metricData.referralId && metricData.runId) {
    // Already built
    metricDoc = metricData;
  } else {
    // Build from raw params
    metricDoc = buildMetricEvent({
      ...metricData,
      tenantId,
    });
  }
  
  // Validate
  const validation = validateMetricEvent(metricDoc);
  if (!validation.valid) {
    console.error('[ERROR] saveMetricEventV2 validation failed:', validation.errors);
    throw new Error(`saveMetricEventV2 validation failed: ${validation.errors.join(', ')}`);
  }
  
  // Ensure tenantId matches
  metricDoc.tenantId = tenantId;
  
  const eventId = metricDoc.eventId || generateMetricId();
  const metricRef = doc(db, PATHS.metricEvent(tenantId, eventId));
  
  await setDoc(metricRef, metricDoc);
  
  return {
    success: true,
    eventId,
  };
};

/**
 * Get metrics for analytics (tenant_admin view)
 * 
 * @param {string} tenantId
 * @param {Object} options - { pageSize, startAfter, outcomeStatus, specialtyId }
 * @returns {Promise<{rows: Array, lastDoc: any, hasMore: boolean}>}
 */
export const getMetricsForAnalytics = async (tenantId, options = {}) => {
  const db = getDb();
  const { pageSize = 100, startAfter = null, outcomeStatus = null, specialtyId = null } = options;
  
  const metricsRef = collection(db, PATHS.col.metrics(tenantId));
  
  let constraints = [orderBy('savedAt', 'desc')];
  
  if (outcomeStatus) {
    constraints.unshift(where('outcomeStatus', '==', normalizeOutcomeStatus(outcomeStatus)));
  }
  
  if (specialtyId) {
    constraints.unshift(where('specialtyId', '==', specialtyId));
  }
  
  if (startAfter) {
    constraints.push(startAfterDoc(startAfter));
  }
  
  constraints.push(limit(pageSize + 1));
  
  const q = query(metricsRef, ...constraints);
  const snapshot = await getDocs(q);
  
  const rows = [];
  let lastDoc = null;
  
  snapshot.forEach((docSnap, index) => {
    if (index < pageSize) {
      const data = cleanDoc(docSnap);
      if (data) {
        data._docRef = docSnap;
        rows.push(data);
      }
      lastDoc = docSnap;
    }
  });
  
  const hasMore = snapshot.size > pageSize;
  
  return { rows, lastDoc, hasMore };
};

/**
 * Get metrics by user (My Submissions metrics view)
 * 
 * @param {string} tenantId
 * @param {string} userId
 * @param {Object} options
 * @returns {Promise<{rows: Array, lastDoc: any, hasMore: boolean}>}
 */
export const getMetricsByUserV2 = async (tenantId, userId, options = {}) => {
  const db = getDb();
  const { pageSize = 50, startAfter = null, outcomeStatus = null } = options;

  // Normalize userId to string (Firebase Auth uses uid, profile uses id)
  const uid = typeof userId === 'string' ? userId : (userId?.uid || userId?.id);

  if (!uid) {
    console.error('[getMetricsByUserV2] Invalid userId:', userId);
    return { rows: [], lastDoc: null, hasMore: false };
  }

  devLog('[getMetricsByUserV2] Querying metrics:', { tenantId, uid, pageSize });

  const metricsRef = collection(db, PATHS.col.metrics(tenantId));

  let constraints = [
    where('createdByUid', '==', uid),
    orderBy('savedAt', 'desc'),
  ];
  
  if (outcomeStatus) {
    constraints.push(where('outcomeStatus', '==', normalizeOutcomeStatus(outcomeStatus)));
  }
  
  if (startAfter) {
    constraints.push(startAfterDoc(startAfter));
  }
  
  constraints.push(limit(pageSize + 1));

  const q = query(metricsRef, ...constraints);
  const snapshot = await getDocs(q);

  devLog('[getMetricsByUserV2] Query results:', {
    snapshotSize: snapshot.size,
    firstDocId: snapshot.docs[0]?.id || 'none',
  });

  // CRITICAL FIX: Use snapshot.docs array, not forEach with index
  // Firestore QuerySnapshot.forEach does NOT provide an index parameter
  const docs = snapshot.docs.slice(0, pageSize);
  const rows = [];

  docs.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) {
      data._docRef = docSnap;
      rows.push(data);
    }
  });

  const lastDoc = docs.length > 0 ? docs[docs.length - 1] : null;
  const hasMore = snapshot.size > pageSize;

  devLog('[getMetricsByUserV2] Returning:', {
    rowCount: rows.length,
    hasMore,
  });

  return { rows, lastDoc, hasMore };
};

/**
 * Get metrics by specialty (Specialist analytics)
 *
 * @param {string} tenantId
 * @param {string} specialtyId
 * @param {Object} options
 * @returns {Promise<{rows: Array, lastDoc: any, hasMore: boolean}>}
 */
export const getMetricsBySpecialtyV2 = async (tenantId, specialtyId, options = {}) => {
  const db = getDb();
  const { pageSize = 50, startAfter = null, outcomeStatus = null } = options;

  const metricsRef = collection(db, PATHS.col.metrics(tenantId));

  let constraints = [
    where('specialtyId', '==', specialtyId),
    orderBy('savedAt', 'desc'),
  ];

  if (outcomeStatus) {
    constraints.push(where('outcomeStatus', '==', outcomeStatus));
  }

  if (startAfter) {
    constraints.push(startAfterDoc(startAfter));
  }

  constraints.push(limit(pageSize + 1));

  const q = query(metricsRef, ...constraints);
  const snapshot = await getDocs(q);

  devLog('[getMetricsBySpecialtyV2] Query results:', {
    specialtyId,
    snapshotSize: snapshot.size,
  });

  // CRITICAL FIX: Use snapshot.docs array, not forEach with index
  // Firestore QuerySnapshot.forEach does NOT provide an index parameter
  const docs = snapshot.docs.slice(0, pageSize);
  const rows = [];

  docs.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) {
      data._docRef = docSnap;
      rows.push(data);
    }
  });

  const lastDoc = docs.length > 0 ? docs[docs.length - 1] : null;
  const hasMore = snapshot.size > pageSize;

  return { rows, lastDoc, hasMore };
};

// ============================================================================
// AUDIT LOG OPERATIONS
// Path: tenants/{tenantId}/referrals/{referralId}/auditLogs/{runId}
// ============================================================================

/**
 * Save an audit log to Firestore (subcollection of referral)
 * 
 * @param {string} tenantId
 * @param {string} referralId
 * @param {Object} auditData - Data from buildAuditLog or raw params
 * @returns {Promise<{success: boolean, runId: string}>}
 * @throws {Error} If validation fails or write fails
 */
export const saveAuditLogV2 = async (tenantId, referralId, auditData) => {
  const db = getDb();
  
  // Build document if raw params provided
  let auditDoc;
  if (auditData.runId && auditData.trace && Array.isArray(auditData.trace)) {
    // Already built
    auditDoc = auditData;
  } else {
    // Build from raw params
    auditDoc = buildAuditLog({
      ...auditData,
      tenantId,
      referralId,
    });
  }
  
  // Validate
  const validation = validateAuditLog(auditDoc);
  if (!validation.valid) {
    console.error('[ERROR] saveAuditLogV2 validation failed:', validation.errors);
    throw new Error(`saveAuditLogV2 validation failed: ${validation.errors.join(', ')}`);
  }
  
  // Ensure IDs match
  auditDoc.tenantId = tenantId;
  auditDoc.referralId = referralId;
  
  const runId = auditDoc.runId;
  const auditRef = doc(db, PATHS.referralAuditLog(tenantId, referralId, runId));
  
  await setDoc(auditRef, auditDoc);
  
  return {
    success: true,
    runId,
  };
};

/**
 * Get audit log for a specific referral and run
 * 
 * @param {string} tenantId
 * @param {string} referralId
 * @param {string} runId
 * @returns {Promise<Object|null>}
 */
export const getAuditLog = async (tenantId, referralId, runId) => {
  const db = getDb();
  const auditRef = doc(db, PATHS.referralAuditLog(tenantId, referralId, runId));
  const snapshot = await getDoc(auditRef);
  return cleanDoc(snapshot);
};

/**
 * Get all audit logs for a referral
 * 
 * @param {string} tenantId
 * @param {string} referralId
 * @returns {Promise<Array>}
 */
export const getAuditLogsForReferral = async (tenantId, referralId) => {
  const db = getDb();
  const logsRef = collection(db, PATHS.col.referralAuditLogs(tenantId, referralId));
  const q = query(logsRef, orderBy('createdAt', 'desc'));
  const snapshot = await getDocs(q);
  
  const logs = [];
  snapshot.forEach((docSnap) => {
    const data = cleanDoc(docSnap);
    if (data) logs.push(data);
  });
  
  return logs;
};

// ============================================================================
// BATCH WRITE - ATOMIC SUBMISSION
// 
// This is the main entry point for submitting a referral.
// It atomically writes all three documents in a single transaction.
// ============================================================================

/**
 * Submit a referral with all associated documents (atomic write)
 * 
 * Writes:
 *   1. ReferralDoc at tenants/{tenantId}/referrals/{referralId}
 *   2. MetricEventDoc at tenants/{tenantId}/metrics/{eventId}
 *   3. AuditLogDoc at tenants/{tenantId}/referrals/{referralId}/auditLogs/{runId}
 * 
 * @param {Object} params
 * @returns {Promise<{success: boolean, referralId: string, eventId: string, runId: string}>}
 * @throws {Error} If any validation or write fails
 */
/**
 * Submit referral with idempotency + retry (BLOCK 9)
 *
 * FIRESTORE WRITES:
 *   - tenants/{tenantId}/submissions/{submissionKey}     (idempotency lock)
 *   - tenants/{tenantId}/referrals/{referralId}
 *   - tenants/{tenantId}/metrics/{eventId}
 *   - tenants/{tenantId}/referrals/{referralId}/auditLogs/{runId}
 *
 * @param {Object} params
 * @returns {Promise<{success: boolean, referralId: string, eventId: string, runId: string}>}
 * @throws {Error} If submission fails after retries
 */
export const submitReferral = async ({
  tenantId,
  specialtyId,
  packId,
  versionId,
  createdByUid,
  createdByName = null,
  createdByEmail = null,
  createdByRole = null,
  patient,
  answers,
  outcome,
  missingFieldIds = [],
  triggeredRedFlagIds = [],
  trace = [],
  telehealthType = null,
  telehealthState = null,
  engineVersion = '1.0.0',
  durationMs = 0,
  runSessionId = null, // OVERRIDE: Optional run session ID to read override from
  override = null, // OVERRIDE: Optional direct override object (alternative to reading from runSession)
  draftReferralId = null, // PHI FLOW: Pre-created draft referral ID (from createDraftReferral)
  existingReferralId = null, // RERUN: When set, update this referral in place instead of creating a new doc
  ehrCandidateUsage = null, // Build 2J P2: EHR suggestion usage → never-PHI audit provenance
}) => {
  const db = getDb();

  // ========================================================================
  // STEP 1: Generate IDs and submission key
  // ========================================================================

  // RERUN MODE: when existingReferralId is provided, update the existing doc in place.
  // A fresh runId is generated so each run produces its own audit log and metric event.
  // The submission lock is skipped — idempotency is inherent in updating a known doc.
  const isRerun = Boolean(existingReferralId);
  const referralId = existingReferralId || draftReferralId || generateReferralId();
  const runId = isRerun ? generateMetricId() : referralId; // Model A for first run; fresh ID for rerun
  const eventId = generateMetricId();
  const submissionKey = isRerun ? null : generateSubmissionKey(tenantId, referralId, createdByUid);

  devLog(`[submitReferral] Starting ${isRerun ? 'RERUN' : 'new'} submission for referralId:`, referralId);

  // ========================================================================
  // Build 2J P2: EHR candidate provenance (Never-PHI audit refs/counts only).
  // Built + structurally guarded BEFORE any Firestore write so an unsafe / oversized
  // payload fails fast with NO partial state (no submission lock, no docs). Returns
  // null when no EHR candidate influenced the referral — manual / non-EHR submits
  // produce no provenance and the audit shape is unchanged. EHR candidates remain
  // SOURCE MATERIAL; this only records WHICH refs influenced the GP-confirmed truth.
  // ========================================================================
  let ehrCandidateProvenance = null;
  if (ehrCandidateUsage && Array.isArray(ehrCandidateUsage.usedCandidateRefs) && ehrCandidateUsage.usedCandidateRefs.length > 0) {
    ehrCandidateProvenance = buildEhrCandidateProvenance({
      packetId: ehrCandidateUsage.packetId,
      capturedByUid: createdByUid,
      capturedAt: serverTimestamp(),
      usedCandidateRefs: ehrCandidateUsage.usedCandidateRefs,
      ignoredOrRejectedCount: ehrCandidateUsage.ignoredOrRejectedCount,
    });
    // Defence-in-depth: the Never-PHI structural guard (forbidden key names +
    // token/email-shaped values) on top of the builder's allowlist + charset caps.
    if (ehrCandidateProvenance) {
      assertSafeNeverPhiDoc(ehrCandidateProvenance, { context: 'audit ehrCandidateProvenance' });
    }
  }

  if (isRerun) {
    // Fail-fast: guard against missing context
    if (!existingReferralId) {
      throw new Error('[submitReferral] RERUN mode requires a valid existingReferralId.');
    }
    devLog('[submitReferral] Rerun mode — will update existing referral:', existingReferralId);
  } else {
    // ========================================================================
    // STEP 2: Check for existing submission (idempotency — first run only)
    // ========================================================================

    const submissionRef = doc(db, PATHS.submission(tenantId, submissionKey));

    try {
      const existingLock = await getDoc(submissionRef);

      if (existingLock.exists()) {
        const lockData = existingLock.data();

        if (lockData.status === 'committed') {
          devLog('[submitReferral] ✅ Submission already committed (idempotent):', lockData.referralId);
          return {
            success: true,
            referralId: lockData.referralId,
            eventId: lockData.eventId,
            runId: lockData.runId,
            receiptToken: lockData.receiptToken || null,
          };
        }

        if (lockData.status === 'pending') {
          console.warn('[submitReferral] ⚠️  Submission in progress, please wait');
          throw new Error('Submission already in progress. Please wait and retry if it fails.');
        }

        if (lockData.status === 'failed') {
          devLog('[submitReferral] Previous submission failed, will retry');
          // Continue to retry
        }
      }
    } catch (err) {
      if (err.code !== 'permission-denied') {
        console.warn('[submitReferral] Could not check existing lock:', err);
      }
      // Continue anyway
    }

    // ========================================================================
    // STEP 3: Create pending submission lock (first run only)
    // ========================================================================

    const lockDoc = buildSubmissionLock({
      tenantId,
      submissionKey,
      referralId,
      runId,
      eventId,
      createdByUid,
      status: 'pending',
      attempts: 1,
    });

    const lockValidation = validateSubmissionLock(lockDoc);
    if (!lockValidation.valid) {
      throw new Error(`Submission lock validation failed: ${lockValidation.errors.join(', ')}`);
    }

    try {
      await setDoc(submissionRef, lockDoc);
      devLog('[submitReferral] Submission lock created');
    } catch (err) {
      console.error('[submitReferral] Failed to create submission lock:', err);
      throw new Error(`Failed to create submission lock: ${err.message}`);
    }
  }

  // ========================================================================
  // STEP 4: Fetch impact settings
  // ========================================================================

  let impactSettings = null;
  try {
    impactSettings = await getImpactSettings(tenantId);
    devLog('[submitReferral] Using impact settings:', impactSettings);
  } catch (err) {
    console.warn('[submitReferral] Could not load impact settings, using defaults:', err);
    impactSettings = DEFAULT_IMPACT_SETTINGS;
  }

  // ========================================================================
  // STEP 5: PHI Safety Validation (BLOCK 13)
  // ========================================================================

  // Sanitize clinical answers BEFORE they enter any Never-PHI sink (referral
  // root, audit-log trace, sessionStorage resume). The Gate submit path does not
  // hold the pack, so sanitizeAnswersForStorage runs pack-less: it redacts
  // high-confidence PHI VALUES (email / phone / SSN-or-MRN-like long numbers /
  // DOB-like dates) from free-text answers while preserving clinical content
  // (clinical timeline/dosage context is kept — see phiScan.redactBlockingPhi).
  const sanitizedAnswers = sanitizeAnswersForStorage(answers, null, DEFAULT_PHI_POLICY);

  // Fail fast — never silently write unsafe state. If a high-confidence PHI value
  // somehow survived sanitization, abort BEFORE any Firestore write.
  assertAnswersStorageSafe(sanitizedAnswers, 'referral.answers');

  devLog('[submitReferral] Clinical answers sanitized for Never-PHI storage');

  // ========================================================================
  // STEP 5b: Fetch override from run session (if runSessionId provided)
  // ========================================================================

  let effectiveOverride = override || null;

  if (!effectiveOverride && runSessionId) {
    try {
      const sessionOverride = await getOverrideFromRunSession(tenantId, runSessionId);
      if (sessionOverride && sessionOverride.used) {
        devLog('[submitReferral] Found override in run session:', sessionOverride.reason);
        effectiveOverride = sessionOverride;
      }
    } catch (err) {
      // Non-blocking: override read failure should not block submission
      console.warn('[submitReferral] Could not read override from runSession (non-blocking):', err);
    }
  }

  if (effectiveOverride) {
    devLog('[submitReferral] Override will be applied:', effectiveOverride.reason);
  }

  // ========================================================================
  // STEP 6: Build and validate documents
  // ========================================================================

  // When override is applied, change outcome status from not_yet to routine/urgent
  // Override finalizes the referral — it should NOT remain as not_yet
  const effectiveOutcomeStatus = (effectiveOverride && effectiveOverride.used)
    ? (triggeredRedFlagIds.length > 0 ? 'urgent' : 'routine')
    : (outcome.status || outcome);

  if (effectiveOverride && effectiveOverride.used) {
    devLog('[submitReferral] Override changes status from', outcome.status, 'to', effectiveOutcomeStatus);
  }

  // Build outcome with override if present
  const outcomeWithOverride = effectiveOverride
    ? { ...outcome, status: effectiveOutcomeStatus, override: effectiveOverride }
    : outcome;

  const referralDoc = buildReferralDoc({
    tenantId,
    specialtyId,
    packId,
    versionId,
    createdByUid,
    patient,
    answers: sanitizedAnswers, // Use sanitized answers
    outcome: outcomeWithOverride,
    missingFieldIds,
    triggeredRedFlagIds,
    completenessScore: outcome.completenessScore || 0,
    referralId,
  });

  // IMPORTANT: If engine decided not_yet but override changed it to routine/urgent,
  // we still need hadNotYet=true so Not-Yet Resolution metrics count this referral.
  // buildReferralDoc only sets hadNotYet when normalizedStatus is 'not_yet', but
  // after override the status is already routine/urgent.
  const originalEngineStatus = outcome.status || outcome;
  if (effectiveOverride && effectiveOverride.used && originalEngineStatus === 'not_yet') {
    referralDoc.hadNotYet = true;
    referralDoc.firstNotYetAt = referralDoc.createdAt;
    devLog('[submitReferral] Set hadNotYet=true (engine was not_yet, overridden to', effectiveOutcomeStatus, ')');
  }

  // RERUN: preserve referral history so Not-Yet completions are flagged as
  // conversions. Query the latest prior metric to derive the next attempt number
  // and the previous outcome status (which drives computed.isConversionEvent).
  // This read runs BEFORE the write transaction below (reads-before-writes intact).
  let rerunAttempt = 1;
  let priorOutcomeStatus = null;
  if (isRerun) {
    // Derive conversion lineage from the REFERRAL DOC (the owner is permitted to
    // read it) — never query the metrics collection (the completer cannot list it).
    // FAIL FAST if the referral can't be read; never silently emit a rerun metric
    // with attempt=1 / missing prevOutcomeStatus (which would not be a conversion).
    let existingSnap;
    try {
      existingSnap = await getDoc(doc(db, PATHS.referral(tenantId, referralId)));
    } catch (err) {
      throw new Error(`[submitReferral] RERUN: failed to read referral ${referralId} — aborting to avoid writing a non-conversion metric: ${err?.message || err}`);
    }
    if (!existingSnap.exists()) {
      throw new Error(`[submitReferral] RERUN: referral ${referralId} not found — cannot determine attempt/prevOutcomeStatus. Aborting.`);
    }
    const existing = existingSnap.data();
    priorOutcomeStatus = existing.outcome?.status ?? null;       // e.g. 'not_yet'
    const priorAttempt = existing.metricAttempt || 1;            // sequential counter on referral root
    rerunAttempt = priorAttempt + 1;
  }

  const metricDoc = buildMetricEvent({
    tenantId,
    specialtyId,
    packId,
    versionId,
    referralId,
    runId,
    createdByUid,
    outcomeStatus: effectiveOutcomeStatus,
    completenessScore: outcome.completenessScore || 0,
    missingFieldIds,
    triggeredRedFlagIds,
    telehealthType,
    telehealthState,
    eventId,
    impactSettings,
    // Include override if present
    overrideUsed: Boolean(effectiveOverride && effectiveOverride.used),
    overrideReasonCode: effectiveOverride?.reason || null,
    override: effectiveOverride || null,
    // RERUN: preserve attempt + prior outcome so a Not-Yet completion is a true
    // conversion (computed.isConversionEvent). First-run submits omit these.
    ...(isRerun ? { attempt: rerunAttempt, prevOutcomeStatus: priorOutcomeStatus } : {}),
  });
  // Flag first-run vs rerun so the metric event CF only increments "Created Today"
  // for genuine new referrals (reruns update an existing referral — not a new creation).
  metricDoc.isFirstRun = !isRerun;

  // S1 — Never-PHI write hardening: the engine trace stringifies answer values
  // (pathway-engine.js), so redact high-confidence PHI from it before it lands in
  // the nested auditLogs and the flat audit_logs analytics copy.
  const sanitizedTrace = redactBlockingPhiDeep(trace);

  const auditDoc = buildAuditLog({
    tenantId,
    referralId,
    runId,
    specialtyId,
    packId,
    versionId,
    outcomeStatus: effectiveOutcomeStatus,
    trace: sanitizedTrace,
    artifacts: [],
    createdByUid,
    createdByName,
    createdByEmail,
    createdByRole,
    engineVersion,
    durationMs,
  });

  // Build 2J P2 — attach the Never-PHI EHR candidate provenance (refs/counts only).
  // The SAME auditDoc is written to BOTH the nested referrals/.../auditLogs/{runId}
  // and the flat audit_logs/{runId} below, so both audit locations stay consistent.
  if (ehrCandidateProvenance) {
    auditDoc.ehrCandidateProvenance = ehrCandidateProvenance;
  }

  // Validate all documents
  const referralValidation = validateReferralDoc(referralDoc);
  if (!referralValidation.valid) {
    throw new Error(`Referral validation failed: ${referralValidation.errors.join(', ')}`);
  }

  // Sensitive-data boundary (hard-fail BEFORE the batch — no partial writes).
  // Rejects PHI/PHI-adjacent/token/secret/raw-FHIR/attachment field names + token/
  // email-shaped values from the Never-PHI referral root. Covers the rerun update
  // too (it writes a subset of referralDoc). Throws typed SENSITIVE_DATA_VIOLATION.
  assertSafeNeverPhiDoc(referralDoc, { context: 'referral root', opaqueKeys: REFERRAL_ROOT_OPAQUE_KEYS });

  const metricValidation = validateMetricEvent(metricDoc);
  if (!metricValidation.valid) {
    throw new Error(`Metric validation failed: ${metricValidation.errors.join(', ')}`);
  }

  const auditValidation = validateAuditLog(auditDoc);
  if (!auditValidation.valid) {
    throw new Error(`Audit validation failed: ${auditValidation.errors.join(', ')}`);
  }

  devLog('[submitReferral] All documents validated');

  // ========================================================================
  // STEP 6: Load impact settings for rollup (BLOCK 12)
  // ========================================================================

  let minutesSavedPerPreventedReferral = 5; // default
  try {
    const impactSettings = await getImpactSettings(tenantId);
    minutesSavedPerPreventedReferral = impactSettings?.assumptions?.minutesSavedPerPreventedReferral || 5;
  } catch (err) {
    console.warn('[submitReferral] Could not load impact settings, using default (5 min):', err);
  }

  // ========================================================================
  // STEP 7: Commit batch with retry (includes daily rollup)
  // ========================================================================

  // submissionRef is only used in first-run mode; declare here for failure handler scope.
  const submissionRef = isRerun ? null : doc(db, PATHS.submission(tenantId, submissionKey));

  const commitBatch = async () => {
    const batch = writeBatch(db);

    const referralRef = doc(db, PATHS.referral(tenantId, referralId));

    if (isRerun) {
      // RERUN: update only mutable fields — preserve createdAt, createdByUid, localId,
      // receiptToken, tenantId, specialtyId, packId, versionId (all sacred).
      // The rerun path is exclusive to NOT_YET referrals; recovery to ROUTINE/URGENT
      // does NOT advance the workflow status. The referral stays in NEEDS_INFO until
      // Intake claims and clears it (recoveryStage drives the recovery lane instead).
      // Therefore workflowStatus stays NEEDS_INFO and workflowStatusEnteredAt is NOT
      // reset (the status did not change — preserve the stuck timer).
      batch.update(referralRef, {
        answers:               referralDoc.answers,
        outcome:               referralDoc.outcome,
        missingFieldIds:       referralDoc.missingFieldIds,
        triggeredRedFlagIds:   referralDoc.triggeredRedFlagIds,
        savedAt:               referralDoc.savedAt,
        runId,                 // updated to the latest run
        ...(referralDoc.visitType            ? { visitType: referralDoc.visitType }                       : {}),
        ...(referralDoc.reasonForEscalation  ? { reasonForEscalation: referralDoc.reasonForEscalation }   : {}),
        ...(referralDoc.visitPurpose         ? { visitPurpose: referralDoc.visitPurpose }                  : {}),
        ...(referralDoc.telehealthContext    ? { telehealthContext: referralDoc.telehealthContext }         : {}),
        ...(referralDoc.age                  ? { age: referralDoc.age }                                   : {}),
        ...(referralDoc.sex                  ? { sex: referralDoc.sex }                                   : {}),
        // Preserve hadNotYet flag if set (engine was not_yet at least once)
        ...(referralDoc.hadNotYet            ? { hadNotYet: true }                                         : {}),
        // Denormalized query field — canonical source is workflow/state.status.
        // ALWAYS NEEDS_INFO on rerun: recovered referrals must not jump to READY_TO_SCHEDULE.
        workflowStatus:          WORKFLOW_STATUS.NEEDS_INFO,
        // Sequential metric-attempt counter (drives rerun attempt numbering).
        metricAttempt: rerunAttempt,
      });
      devLog('[submitReferral] Rerun: updating existing referral at', `tenants/${tenantId}/referrals/${referralId}`);
    } else {
      // FIRST RUN: create the referral doc.
      // Include denormalized workflowStatus so the GP inbox can query by workflow state
      // without subcollection joins. Canonical source remains workflow/state.status.
      const initialWorkflowStatus = effectiveOutcomeStatus === 'not_yet'
        ? WORKFLOW_STATUS.NEEDS_INFO
        : WORKFLOW_STATUS.NEW;
      batch.set(referralRef, {
        ...referralDoc,
        workflowStatus:          initialWorkflowStatus,
        workflowStatusEnteredAt: serverTimestamp(),
        // First run = metric attempt 1; reruns increment this on the referral root.
        metricAttempt: 1,
      });
    }

    const metricRef = doc(db, PATHS.metricEvent(tenantId, eventId));
    batch.set(metricRef, metricDoc);

    // Write audit log to nested subcollection (canonical path)
    const auditRef = doc(db, PATHS.referralAuditLog(tenantId, referralId, runId));
    batch.set(auditRef, auditDoc);
    devLog('[submitReferral] Added to batch: nested auditLog at', `tenants/${tenantId}/referrals/${referralId}/auditLogs/${runId}`);

    // ALSO write to flat audit_logs collection for easy tenant-wide querying
    const flatAuditRef = doc(db, PATHS.auditLogFlat(tenantId, runId));
    batch.set(flatAuditRef, auditDoc);
    devLog('[submitReferral] Added to batch: FLAT auditLog at', `tenants/${tenantId}/audit_logs/${runId}`);

    if (!isRerun) {
      // Mark first-run submission lock as committed
      batch.update(submissionRef, {
        status: 'committed',
        updatedAt: toTimestamp(),
      });
    }

    // BLOCK 12: Daily rollup (NOW HANDLED BY CLOUD FUNCTION)
    // ⚠️ Rollup increment removed from client - now done server-side via
    // onMetricEventCreate Cloud Function trigger for reliability + idempotency
    // See: functions/index.js -> onMetricEventCreate_updateDailyRollup
    // await incrementDailyRollup(batch, tenantId, metricDoc, minutesSavedPerPreventedReferral);

    if (isRerun) {
      // RERUN WORKFLOW: update existing workflow/state + append STATUS_CHANGED event.
      updateWorkflowOnRerun(batch, {
        tenantId,
        referralId,
        actorUid:      createdByUid,
        actorRole:     createdByRole || 'gp_employee',
        outcomeStatus: effectiveOutcomeStatus,
      });
    } else {
      // FIRST RUN WORKFLOW: create workflow/state + WORKFLOW_CREATED event.
      // This guarantees Invariant 1: every referral has a workflow/state doc.
      ensureWorkflowOnSubmit(batch, {
        tenantId,
        referralId,
        createdByUid,
        outcomeStatus: effectiveOutcomeStatus,
      });
    }

    await batch.commit();
  };

  // Force-refresh JWT token before batch commit to ensure role/tenantId claims are current.
  // Stale cached tokens cause PERMISSION_DENIED even when the user has the correct role.
  try {
    const fbAuth = getAuth(getApp());
    if (fbAuth.currentUser) {
      await fbAuth.currentUser.getIdToken(/* forceRefresh= */ true);
      devLog('[submitReferral] JWT token refreshed for fresh claims');
    }
  } catch (tokenErr) {
    console.warn('[submitReferral] Token refresh failed (non-blocking):', tokenErr);
  }

  try {
    await retryWithBackoff(commitBatch, { maxAttempts: 3 });

    devLog('[submitReferral] ✅ Batch committed successfully:');
    devLog('  - ReferralDoc:', `tenants/${tenantId}/referrals/${referralId}`);
    devLog('  - MetricDoc:', `tenants/${tenantId}/metrics/${eventId}`);
    devLog('  - AuditDoc (nested):', `tenants/${tenantId}/referrals/${referralId}/auditLogs/${runId}`);
    devLog('  - AuditDoc (flat):', `tenants/${tenantId}/audit_logs/${runId}`);

    return {
      success: true,
      referralId,
      eventId,
      runId,
      receiptToken: referralDoc.receiptToken || null,
    };
  } catch (err) {
    console.error('[submitReferral] ❌ Batch commit failed after retries:', err);

    // Mark first-run submission lock as failed (skipped for reruns — no lock exists)
    if (!isRerun && submissionRef) {
      try {
        await updateDoc(submissionRef, {
          status: 'failed',
          lastError: {
            code: err.code || 'unknown',
            message: err.message || 'Batch commit failed',
          },
          updatedAt: toTimestamp(),
        });
      } catch (updateErr) {
        console.error('[submitReferral] Failed to update submission lock:', updateErr);
      }
    }

    throw new Error(`Failed to submit referral: ${err.message}`);
  }
};

// ============================================================================
// ANALYTICS QUERIES
// ============================================================================

/**
 * Get outcome counts for analytics dashboard
 * 
 * @param {string} tenantId
 * @param {Object} filters - { specialtyId, packId, startDate, endDate }
 * @returns {Promise<{urgent: number, routine: number, not_yet: number, total: number}>}
 */
export const getOutcomeCounts = async (tenantId, filters = {}) => {
  const db = getDb();
  const { specialtyId = null, packId = null } = filters;
  
  // Note: For large datasets, consider using aggregation queries or Cloud Functions
  // This implementation fetches all metrics and counts client-side
  const metricsRef = collection(db, PATHS.col.metrics(tenantId));
  
  let constraints = [];
  
  if (specialtyId) {
    constraints.push(where('specialtyId', '==', specialtyId));
  }
  
  if (packId) {
    constraints.push(where('packId', '==', packId));
  }
  
  const q = constraints.length > 0 
    ? query(metricsRef, ...constraints)
    : query(metricsRef);
    
  const snapshot = await getDocs(q);
  
  const counts = {
    urgent: 0,
    routine: 0,
    not_yet: 0,
    total: 0,
  };
  
  snapshot.forEach((docSnap) => {
    const data = docSnap.data();
    const status = data.outcomeStatus || 'routine';
    counts.total++;
    
    if (status === 'urgent') counts.urgent++;
    else if (status === 'routine') counts.routine++;
    else if (status === 'not_yet') counts.not_yet++;
  });
  
  return counts;
};

/**
 * Get outcome rates for analytics
 *
 * @param {string} tenantId
 * @param {Object} filters
 * @returns {Promise<{urgentRate: number, routineRate: number, notYetRate: number}>}
 */
export const getOutcomeRates = async (tenantId, filters = {}) => {
  const counts = await getOutcomeCounts(tenantId, filters);

  if (counts.total === 0) {
    return { urgentRate: 0, routineRate: 0, notYetRate: 0 };
  }

  return {
    urgentRate: Math.round((counts.urgent / counts.total) * 100),
    routineRate: Math.round((counts.routine / counts.total) * 100),
    notYetRate: Math.round((counts.not_yet / counts.total) * 100),
  };
};

// ============================================================================
// BLOCK 5: CONVERSION TRACKING ANALYTICS
// ============================================================================

/**
 * Get Not-Yet → Completed conversion metrics (BLOCK 5)
 *
 * Aggregation logic (tenant-scoped):
 * 1. Query all MetricEvents in time window, ordered by savedAt
 * 2. Group events by referralId, sort each group by savedAt asc
 * 3. For each referralId:
 *    - Find first not_yet event (if none exists, ignore this referral)
 *    - Assign referral to specialtyId bucket from that first not_yet event
 *    - Check if a later event exists where outcomeStatus is routine/urgent
 *    - If yes, count exactly 1 conversion for that referralId in that specialty bucket
 * 4. Per specialty bucket:
 *    - total_not_yet_referrals = unique referralIds with a not_yet
 *    - converted_referrals = referralIds that later completed
 *    - conversion_rate = converted_referrals / total_not_yet_referrals
 *
 * FIRESTORE READ:
 *   - tenants/{tenantId}/metrics (query by savedAt, optionally filtered by specialty/pack)
 *
 * @param {string} tenantId - Tenant ID
 * @param {Object} filters - Optional filters { specialtyId, packId, startDate, endDate }
 * @returns {Promise<{notYetCases: number, convertedCases: number, conversionRate: number, bySpecialty: Object}>}
 */
export const getConversionMetrics = async (tenantId, filters = {}) => {
  const db = getDb();
  const { specialtyId = null, packId = null } = filters;

  devLog('[getConversionMetrics] Computing for tenant:', tenantId, 'filters:', filters);

  try {
    // ====================================================================
    // 1. Query referrals to get hadNotYet + outcome.isOverride + outcome.status
    // ====================================================================
    const referralsRef = collection(db, PATHS.col.referrals(tenantId));
    const referralsSnap = await getDocs(referralsRef);

    // Build a map of referralId -> referral data for lookups
    const referralDataMap = new Map();
    referralsSnap.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.referralId) {
        referralDataMap.set(data.referralId, {
          hadNotYet: Boolean(data.hadNotYet),
          isOverride: Boolean(data.outcome?.isOverride),
          resolvedFromInbox: Boolean(data.resolvedFromInbox),
          outcomeStatus: data.outcome?.status,
          specialtyId: data.specialtyId,
        });
      }
    });

    // ====================================================================
    // 2. Query metrics for attempt-based conversion tracking
    // ====================================================================
    const metricsRef = collection(db, PATHS.col.metrics(tenantId));

    let constraints = [];
    if (specialtyId) {
      constraints.push(where('specialtyId', '==', specialtyId));
    }
    if (packId) {
      constraints.push(where('packId', '==', packId));
    }

    const q = constraints.length > 0
      ? query(metricsRef, ...constraints, orderBy('savedAt', 'asc'))
      : query(metricsRef, orderBy('savedAt', 'asc'));

    const snapshot = await getDocs(q);

    // Group metrics by referralId
    const metricsByReferral = new Map();
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      const refId = data.referralId;
      if (!refId) return;
      if (!metricsByReferral.has(refId)) {
        metricsByReferral.set(refId, []);
      }
      metricsByReferral.get(refId).push({
        outcomeStatus: data.outcomeStatus,
        specialtyId: data.specialtyId,
        overrideUsed: Boolean(data.overrideUsed),
        savedAt: data.savedAt,
        attempt: data.attempt || 1,
      });
    });

    // ====================================================================
    // 3. Compute SPLIT metrics
    // ====================================================================

    // --- A) Overall Override Rate (all finalized referrals) ---
    let finalizedCount = 0;
    let overridesFinalizedCount = 0;

    referralDataMap.forEach((ref) => {
      if (ref.outcomeStatus === 'routine' || ref.outcomeStatus === 'urgent') {
        finalizedCount++;
        if (ref.isOverride) {
          overridesFinalizedCount++;
        }
      }
    });

    const overallOverrideRate = finalizedCount > 0
      ? Math.round((overridesFinalizedCount / finalizedCount) * 1000) / 10
      : 0;

    // --- B) Not-Yet Resolution metrics ---
    // IMPORTANT: Not-Yet Conversion tracks only INBOX cases — referrals that
    // actually sat in the not-yet inbox. Referrals overridden on the first run
    // (hadNotYet=true + isOverride=true) never reached the inbox, so they are
    // counted ONLY in the Override Rate card, not in the conversion funnel.
    let notYetCases = 0;              // inbox cases (hadNotYet + NOT first-run override)
    let notYetResolvedCount = 0;      // inbox cases that got finalized (routine/urgent)
    let notYetOverridesCount = 0;     // hadNotYet + isOverride + finalized (first-run overrides ONLY)
    let notYetConvertedEngineCount = 0; // inbox cases finalized via engine (not override)
    let notYetInboxOverridesCount = 0;  // inbox cases finalized via override from inbox
    let notYetStillPendingCount = 0;  // inbox cases still not_yet

    const specialtyBuckets = new Map();

    referralDataMap.forEach((ref, refId) => {
      if (!ref.hadNotYet) return;

      // Apply specialty filter if provided
      if (specialtyId && ref.specialtyId !== specialtyId) return;

      const isFinalized = ref.outcomeStatus === 'routine' || ref.outcomeStatus === 'urgent';

      // First-run overrides: engine said not_yet but user overrode on first submission.
      // These never entered the inbox — count separately for Override Rate only.
      // Key distinction: first-run overrides have isOverride=true but resolvedFromInbox=false
      // Inbox overrides have isOverride=true AND resolvedFromInbox=true
      if (ref.isOverride && isFinalized && !ref.resolvedFromInbox) {
        notYetOverridesCount++;
        // Also track in specialty buckets for override stats
        const bucket = ref.specialtyId || 'unknown';
        if (!specialtyBuckets.has(bucket)) {
          specialtyBuckets.set(bucket, {
            notYetCases: 0,
            notYetResolved: 0,
            notYetOverrides: 0,
            notYetInboxOverrides: 0,
            notYetEngineConverted: 0,
          });
        }
        specialtyBuckets.get(bucket).notYetOverrides++;
        return; // Do NOT count in notYetCases / conversion funnel
      }

      // Genuine inbox cases (either still pending, resolved via engine, or resolved via inbox override)
      notYetCases++;

      const bucket = ref.specialtyId || 'unknown';
      if (!specialtyBuckets.has(bucket)) {
        specialtyBuckets.set(bucket, {
          notYetCases: 0,
          notYetResolved: 0,
          notYetOverrides: 0,
          notYetInboxOverrides: 0,
          notYetEngineConverted: 0,
        });
      }
      const stats = specialtyBuckets.get(bucket);
      stats.notYetCases++;

      if (isFinalized) {
        notYetResolvedCount++;
        stats.notYetResolved++;

        // Distinguish: resolved via inbox override vs engine re-run
        if (ref.isOverride && ref.resolvedFromInbox) {
          // Inbox override: overridden from the Not-Yet Inbox
          notYetInboxOverridesCount++;
          stats.notYetInboxOverrides++;
        } else {
          // Engine conversion: resolved from inbox via subsequent engine run
          notYetConvertedEngineCount++;
          stats.notYetEngineConverted++;
        }
      } else if (ref.outcomeStatus === 'not_yet') {
        notYetStillPendingCount++;
      }
    });

    // Total not-yet referrals = inbox cases + first-run overrides (all where engine said not_yet)
    const totalNotYetReferrals = notYetCases + notYetOverridesCount;

    // Override rate: overridden / all not-yet referrals
    const notYetOverrideRate = totalNotYetReferrals > 0
      ? Math.round((notYetOverridesCount / totalNotYetReferrals) * 1000) / 10
      : 0;

    // Engine conversion rate: inbox conversions / inbox cases
    const notYetEngineConversionRate = notYetCases > 0
      ? Math.round((notYetConvertedEngineCount / notYetCases) * 1000) / 10
      : 0;

    // --- C) Legacy conversion rate (backward compat) ---
    // Attempt-based conversion (from metrics): referrals that had not_yet then routine/urgent
    let legacyNotYetCases = 0;
    let legacyConvertedCases = 0;

    metricsByReferral.forEach((events) => {
      const firstNotYet = events.find(e => e.outcomeStatus === 'not_yet');
      if (!firstNotYet) return;
      legacyNotYetCases++;
      const firstNotYetIndex = events.indexOf(firstNotYet);
      const laterEvents = events.slice(firstNotYetIndex + 1);
      const hasConversion = laterEvents.some(e =>
        e.outcomeStatus === 'routine' || e.outcomeStatus === 'urgent'
      );
      if (hasConversion) legacyConvertedCases++;
    });

    const overallConversionRate = legacyNotYetCases > 0
      ? Math.round((legacyConvertedCases / legacyNotYetCases) * 100)
      : 0;

    // --- D) By specialty breakdown ---
    const bySpecialty = {};
    specialtyBuckets.forEach((stats, specId) => {
      bySpecialty[specId] = {
        totalNotYetReferrals: stats.notYetCases,
        convertedReferrals: stats.notYetEngineConverted,
        overrideReferrals: stats.notYetOverrides,
        conversionRate: stats.notYetCases > 0
          ? Math.round((stats.notYetEngineConverted / stats.notYetCases) * 100)
          : 0,
      };
    });

    devLog('[getConversionMetrics] ✅ Results:', {
      // Legacy (backward compat)
      notYetCases: legacyNotYetCases,
      convertedCases: legacyConvertedCases,
      conversionRate: overallConversionRate,
      // NEW: Split override metrics
      overallOverrideRate,
      finalizedCount,
      overridesFinalizedCount,
      notYetResolution: {
        notYetCases,
        notYetResolvedCount,
        notYetConvertedEngineCount,
        notYetOverridesCount,
        notYetInboxOverridesCount,
        notYetStillPendingCount,
        notYetOverrideRate,
        notYetEngineConversionRate,
      },
    });

    return {
      // Legacy fields (backward compat for existing UI)
      notYetCases: legacyNotYetCases,
      convertedCases: legacyConvertedCases,
      conversionRate: overallConversionRate,
      bySpecialty,

      // NEW: Overall Override Rate
      overallOverride: {
        finalizedCount,
        overridesFinalizedCount,
        overrideRate: overallOverrideRate,
      },

      // NEW: Not-Yet Resolution (split)
      notYetResolution: {
        notYetCases,                         // inbox cases only (excludes first-run overrides)
        notYetResolvedCount,                 // inbox cases finalized (routine/urgent)
        notYetConvertedEngineCount,          // inbox cases resolved via engine re-run
        notYetOverridesCount,                // first-run overrides (engine=not_yet, user overrode)
        notYetInboxOverridesCount,           // inbox cases resolved via override from inbox
        notYetStillPendingCount,             // inbox cases still not_yet
        totalNotYetReferrals,               // all referrals where engine said not_yet (inbox + overrides)
        notYetOverrideRate,                  // overrides / totalNotYetReferrals
        notYetEngineConversionRate,          // engine conversion share of inbox not-yet cases
      },
    };
  } catch (err) {
    console.error('[getConversionMetrics] ❌ Query failed:', err);
    throw new Error(`Failed to compute conversion metrics: ${err.message}`);
  }
};

/**
 * Get metrics by referralId (for conversion tracking) (BLOCK 5)
 *
 * Returns all attempts for a specific referral, sorted by attempt number
 *
 * FIRESTORE READ:
 *   - tenants/{tenantId}/metrics (query by referralId, order by attempt)
 *
 * @param {string} tenantId - Tenant ID
 * @param {string} referralId - Referral ID
 * @returns {Promise<Array>} Array of metric events for this referral
 */
export const getMetricsByReferralId = async (tenantId, referralId) => {
  const db = getDb();

  devLog('[getMetricsByReferralId] Loading for referral:', referralId);

  try {
    const metricsRef = collection(db, PATHS.col.metrics(tenantId));
    const q = query(
      metricsRef,
      where('referralId', '==', referralId),
      orderBy('attempt', 'desc')
    );

    const snapshot = await getDocs(q);
    const metrics = [];

    snapshot.forEach((docSnap) => {
      metrics.push({
        id: docSnap.id,
        ...docSnap.data(),
      });
    });

    devLog('[getMetricsByReferralId] ✅ Found', metrics.length, 'attempts');

    return metrics;
  } catch (err) {
    console.error('[getMetricsByReferralId] ❌ Query failed:', err);
    throw new Error(`Failed to get metrics by referralId: ${err.message}`);
  }
};

// ============================================================================
// BLOCK 2: RE-RUN GATE (PERSISTENCE)
// ============================================================================

/**
 * Re-run the gate for an existing referral (BLOCK 2)
 *
 * This writes a NEW MetricEventDoc with incremented attempt number
 * and optionally creates a new AuditLogDoc for the rerun
 *
 * FIRESTORE WRITES:
 *   - tenants/{tenantId}/metrics/{eventId}                        (NEW event doc)
 *   - tenants/{tenantId}/referrals/{referralId}/auditLogs/{runId} (NEW audit doc)
 *
 * @param {Object} params
 * @param {string} params.tenantId - Tenant ID
 * @param {string} params.referralId - Referral ID from original submission
 * @param {Object} params.decision - New decision result from rerunPathwayDecision
 * @param {Object} params.pack - Normalized pack
 * @param {string} params.createdByUid - User UID
 * @param {number} params.previousAttempt - Previous attempt number
 * @param {string} params.previousOutcomeStatus - Previous outcome status
 * @param {Object} params.severitySignals - Severity signals (fieldId -> int 0..10)
 * @param {Object} params.telehealthType - Optional telehealth type
 * @param {Object} params.telehealthState - Optional telehealth state
 * @param {string} params.engineVersion - Engine version
 * @returns {Promise<{eventId: string, runId: string, attempt: number}>}
 * @throws {Error} If write fails
 */
/**
 * BLOCK 2 HARDENING: Atomic Re-run Gate (Transaction + Lock Pattern)
 *
 * Prevents race condition from double-click by using Firestore transaction
 * to atomically:
 *   1. Check/create lock document
 *   2. Query max attempt number from existing metrics
 *   3. Create new metric event with attempt = maxAttempt + 1
 *   4. Create audit log
 *
 * FIRESTORE PATHS:
 *   - READ/WRITE: tenants/{tenantId}/rerunLocks/{referralId}
 *   - READ:       tenants/{tenantId}/metrics (query by referralId)
 *   - WRITE:      tenants/{tenantId}/metrics/{eventId}
 *   - WRITE:      tenants/{tenantId}/referrals/{referralId}/auditLogs/{runId}
 *
 * INVARIANTS:
 *   - Lock expires after 2 minutes (safety release)
 *   - Exactly one rerun succeeds per click (no duplicates)
 *   - Attempt numbers are sequential without gaps
 *
 * @param {Object} params - Rerun parameters
 * @returns {Promise<{eventId, runId, attempt, outcomeStatus}>}
 * @throws {Error} If lock exists, query fails, or write fails
 */
export const rerunReferralGate = async ({
  tenantId,
  referralId,
  decision,
  pack,
  createdByUid,
  createdByName = null,
  createdByEmail = null,
  createdByRole = null,
  previousAttempt = 1,  // ⚠️ NO LONGER USED - we query max attempt in transaction
  previousOutcomeStatus = null,
  severitySignals = null,
  telehealthType = null,
  telehealthState = null,
  engineVersion = '2.0.0',
}) => {
  const db = getDb();

  devLog('[rerunReferralGate] Starting ATOMIC re-run for referral:', referralId);

  // ========================================================================
  // STEP 0: Fetch impact settings BEFORE transaction (read-only, cacheable)
  // ========================================================================

  let impactSettings = null;
  try {
    impactSettings = await getImpactSettings(tenantId);
    devLog('[rerunReferralGate] Using impact settings:', impactSettings);
  } catch (err) {
    console.warn('[rerunReferralGate] Could not load impact settings, using defaults:', err);
    impactSettings = DEFAULT_IMPACT_SETTINGS;
  }

  // ========================================================================
  // STEP 1: ATOMIC TRANSACTION (Lock + Query Max Attempt + Write)
  // ========================================================================

  let result = null;

  try {
    result = await runTransaction(db, async (transaction) => {
      const lockRef = doc(db, PATHS.rerunLock(tenantId, referralId));
      const metricsRef = collection(db, PATHS.col.metrics(tenantId));

      // --------------------------------------------------------------------
      // 1a. Check lock document
      // --------------------------------------------------------------------

      const lockDoc = await transaction.get(lockRef);

      if (lockDoc.exists()) {
        const lockData = lockDoc.data();
        const now = Timestamp.now();
        const expiresAt = lockData.expiresAt;

        // If lock is still active (not expired), abort
        if (expiresAt && expiresAt.toMillis() > now.toMillis()) {
          const secondsRemaining = Math.ceil((expiresAt.toMillis() - now.toMillis()) / 1000);
          throw new Error(
            `Rerun already in progress. Please wait ${secondsRemaining} seconds and try again.`
          );
        }

        devLog('[rerunReferralGate] Lock expired, proceeding...');
      }

      // --------------------------------------------------------------------
      // 1b. Query existing metrics to find max attempt number
      // --------------------------------------------------------------------

      const metricsQuery = query(
        metricsRef,
        where('referralId', '==', referralId),
        orderBy('attempt', 'desc'),
        limit(1)
      );

      const metricsSnapshot = await getDocs(metricsQuery);

      let maxAttempt = 0;
      let prevOutcome = previousOutcomeStatus;

      if (!metricsSnapshot.empty) {
        const latestMetric = metricsSnapshot.docs[0].data();
        maxAttempt = latestMetric.attempt || 0;
        prevOutcome = latestMetric.outcomeStatus || previousOutcomeStatus;

        devLog('[rerunReferralGate] Found max attempt:', maxAttempt, 'prevOutcome:', prevOutcome);
      } else {
        devLog('[rerunReferralGate] No existing metrics found, starting from attempt 1');
      }

      // --------------------------------------------------------------------
      // 1c. Build NEW MetricEventDoc with incremented attempt
      // --------------------------------------------------------------------

      const attempt = maxAttempt + 1;
      const newRunId = `${referralId}-attempt-${attempt}`;

      const metricDoc = buildMetricEvent({
        tenantId,
        specialtyId: pack.specialtyId,
        packId: pack.packId,
        versionId: pack.versionId,
        referralId,
        runId: newRunId,
        createdByUid,
        outcomeStatus: decision.outcomeStatus,
        completenessScore: decision.completenessScore || 0,
        missingFieldIds: decision.missingRequiredFieldIds || [],
        triggeredRedFlagIds: decision.triggeredRedFlagIds || [],
        telehealthType,
        telehealthState,
        pdfGenerated: false,
        overrideUsed: false,
        attempt,
        prevOutcomeStatus: prevOutcome,
        severitySignals,
        impactSettings,
      });

      const validation = validateMetricEvent(metricDoc);
      if (!validation.valid) {
        throw new Error(`Metric validation failed: ${validation.errors.join(', ')}`);
      }

      const metricRef = doc(db, PATHS.metricEvent(tenantId, metricDoc.eventId));

      devLog('[rerunReferralGate] Will write MetricEventDoc with attempt:', attempt);

      // --------------------------------------------------------------------
      // 1d. Build NEW AuditLogDoc
      // --------------------------------------------------------------------

      const auditDoc = buildAuditLog({
        tenantId,
        referralId,
        runId: newRunId,
        specialtyId: pack.specialtyId,
        packId: pack.packId,
        versionId: pack.versionId,
        outcomeStatus: decision.outcomeStatus,
        trace: decision.trace || [],
        artifacts: decision.notYetNextSteps?.flatMap(s => s.requiredArtifacts || []) || [],
        createdByUid,
        createdByName,
        createdByEmail,
        createdByRole,
        engineVersion,
        durationMs: decision.durationMs || 0,
        attempt,
      });

      const auditValidation = validateAuditLog(auditDoc);
      if (!auditValidation.valid) {
        throw new Error(`Audit log validation failed: ${auditValidation.errors.join(', ')}`);
      }

      // Write audit log to nested subcollection (canonical path)
      const auditRef = doc(db, PATHS.referralAuditLog(tenantId, referralId, newRunId));
      // ALSO write to flat audit_logs collection for easy tenant-wide querying
      const flatAuditRef = doc(db, PATHS.auditLogFlat(tenantId, newRunId));

      devLog('[rerunReferralGate] Will write AuditLogDoc:', newRunId);

      // --------------------------------------------------------------------
      // 1e. Create/update lock document with expiration
      // --------------------------------------------------------------------

      const now = Timestamp.now();
      const expiresAt = Timestamp.fromMillis(now.toMillis() + 2 * 60 * 1000); // 2 minutes

      const lockData = {
        tenantId,
        referralId,
        locked: true,
        lockType: 'rerun',
        lockedByUserId: createdByUid,
        lockedAt: now,
        expiresAt,
        activeAttemptId: metricDoc.eventId,
        attempt,
      };

      // --------------------------------------------------------------------
      // 1f. ATOMIC WRITES (all succeed or all fail)
      // --------------------------------------------------------------------

      transaction.set(lockRef, lockData);
      transaction.set(metricRef, metricDoc);
      transaction.set(auditRef, auditDoc);
      transaction.set(flatAuditRef, auditDoc); // Also write to flat collection

      devLog('[rerunReferralGate] Transaction prepared with attempt:', attempt);

      return {
        eventId: metricDoc.eventId,
        runId: newRunId,
        attempt,
        outcomeStatus: decision.outcomeStatus,
      };
    });

    devLog('[rerunReferralGate] ✅ Transaction committed successfully, attempt:', result.attempt);

    return result;

  } catch (err) {
    console.error('[rerunReferralGate] ❌ Transaction failed:', err);

    // Fail-fast: rethrow with clear message
    if (err.message.includes('Rerun already in progress')) {
      throw err; // User-facing error, keep as-is
    }

    throw new Error(`Failed to save re-run: ${err.message}`);
  }
};

// ============================================================================
// BLOCK 3: PDF GENERATED TRACKING
// ============================================================================

/**
 * Mark a metrics event as having generated PDF (BLOCK 3)
 *
 * This updates ONLY the pdfGenerated field (and optionally pdfGeneratedAt)
 * Does NOT store PDF binary or text content
 *
 * FIRESTORE WRITE:
 *   - tenants/{tenantId}/metrics/{eventId} (update pdfGenerated=true)
 *
 * @param {string} tenantId - Tenant ID
 * @param {string} eventId - Metrics event ID
 * @returns {Promise<void>}
 * @throws {Error} If update fails
 */
export const markPdfGenerated = async (tenantId, eventId) => {
  const db = getDb();

  devLog('[markPdfGenerated] Marking PDF generated for event:', eventId);

  try {
    const metricRef = doc(db, PATHS.metricEvent(tenantId, eventId));

    await updateDoc(metricRef, {
      pdfGenerated: true,
      pdfGeneratedAt: toTimestamp(),
    });

    devLog('[markPdfGenerated] ✅ Updated successfully');
  } catch (err) {
    console.error('[markPdfGenerated] ❌ Update failed:', err);
    throw new Error(`Failed to mark PDF generated: ${err.message}`);
  }
};

// ============================================================================
// IMPACT SETTINGS PERSISTENCE (BLOCK 4)
// ============================================================================

/**
 * Get tenant impact settings (BLOCK 4)
 *
 * Reads impact settings from tenants/{tenantId}/settings/impact
 * Returns DEFAULT_IMPACT_SETTINGS if doc doesn't exist or fails
 *
 * FIRESTORE READ:
 *   - tenants/{tenantId}/settings/impact
 *
 * @param {string} tenantId - Tenant ID
 * @returns {Promise<Object>} Impact settings object
 */
export const getImpactSettings = async (tenantId) => {
  const db = getDb();

  devLog('[getImpactSettings] Loading for tenant:', tenantId);

  try {
    const settingsRef = doc(db, PATHS.impactSettings(tenantId));
    const settingsSnap = await getDoc(settingsRef);

    if (settingsSnap.exists()) {
      const settings = settingsSnap.data();
      devLog('[getImpactSettings] ✅ Found settings:', settings);
      return settings;
    } else {
      console.warn('[getImpactSettings] ⚠️ No settings found, using defaults');
      return DEFAULT_IMPACT_SETTINGS;
    }
  } catch (err) {
    console.error('[getImpactSettings] ❌ Read failed:', err);
    console.warn('[getImpactSettings] Using default settings as fallback');
    return DEFAULT_IMPACT_SETTINGS;
  }
};

/**
 * Save tenant impact settings (BLOCK 4)
 *
 * Writes impact settings to tenants/{tenantId}/settings/impact
 * Only platform_admin can write (enforced by Firestore rules)
 *
 * FIRESTORE WRITE:
 *   - tenants/{tenantId}/settings/impact
 *
 * @param {string} tenantId - Tenant ID
 * @param {string} updatedByUid - User UID making the update
 * @param {Object} settings - Settings object
 * @param {number} settings.minutesSavedPerPreventedReferral
 * @param {number} settings.minutesSpentPerIncompleteReferral
 * @param {Array<string>} settings.completedOutcomeStatuses
 * @param {Array<string>} settings.preventedOutcomeStatuses
 * @param {boolean} settings.enableTelehealthContext
 * @returns {Promise<void>}
 * @throws {Error} If write fails or validation fails
 */
export const saveImpactSettings = async (tenantId, updatedByUid, settings) => {
  const db = getDb();

  devLog('[saveImpactSettings] Saving for tenant:', tenantId);

  try {
    // Build and validate
    const settingsDoc = buildImpactSettings({
      tenantId,
      updatedByUid,
      minutesSavedPerPreventedReferral: settings.minutesSavedPerPreventedReferral,
      minutesSpentPerIncompleteReferral: settings.minutesSpentPerIncompleteReferral,
      completedOutcomeStatuses: settings.completedOutcomeStatuses,
      preventedOutcomeStatuses: settings.preventedOutcomeStatuses,
      enableTelehealthContext: settings.enableTelehealthContext,
      sla: settings.sla,
      businessHours: settings.businessHours,
    });

    const validation = validateImpactSettings(settingsDoc);
    if (!validation.valid) {
      throw new Error(`Impact settings validation failed: ${validation.errors.join(', ')}`);
    }

    // Write to Firestore
    const settingsRef = doc(db, PATHS.impactSettings(tenantId));
    await setDoc(settingsRef, settingsDoc);

    devLog('[saveImpactSettings] ✅ Saved successfully');
  } catch (err) {
    console.error('[saveImpactSettings] ❌ Save failed:', err);
    throw new Error(`Failed to save impact settings: ${err.message}`);
  }
};

// ============================================================================
// BLOCK 13: PHI POLICY SETTINGS PERSISTENCE (Pilot Mode)
// ============================================================================

/**
 * Get tenant PHI policy settings (BLOCK 13 - Pilot Mode)
 *
 * Reads PHI policy from tenants/{tenantId}/settings/phiPolicy
 * Returns DEFAULT_PHI_POLICY if doc doesn't exist or fails
 *
 * FIRESTORE READ:
 *   - tenants/{tenantId}/settings/phiPolicy
 *
 * @param {string} tenantId - Tenant ID
 * @returns {Promise<Object>} PHI policy object
 */
export const getPhiPolicy = async (tenantId) => {
  const db = getDb();

  devLog('[getPhiPolicy] Loading for tenant:', tenantId);

  try {
    const policyRef = doc(db, PATHS.phiPolicy(tenantId));
    const policySnap = await getDoc(policyRef);

    if (policySnap.exists()) {
      const policy = policySnap.data();
      devLog('[getPhiPolicy] ✅ Found policy:', policy);
      return policy;
    } else {
      console.warn('[getPhiPolicy] ⚠️ No policy found, using defaults');
      return DEFAULT_PHI_POLICY;
    }
  } catch (err) {
    console.error('[getPhiPolicy] ❌ Read failed:', err);
    console.warn('[getPhiPolicy] Using default policy as fallback');
    return DEFAULT_PHI_POLICY;
  }
};

/**
 * Save tenant PHI policy settings (BLOCK 13 - Pilot Mode)
 *
 * Writes PHI policy to tenants/{tenantId}/settings/phiPolicy
 * Only platform_admin can write (enforced by Firestore rules)
 *
 * FIRESTORE WRITE:
 *   - tenants/{tenantId}/settings/phiPolicy
 *
 * @param {string} tenantId - Tenant ID
 * @param {string} updatedByUid - User UID making the update
 * @param {Object} settings - Policy settings object
 * @param {boolean} settings.pilotModeEnabled
 * @param {string} settings.textAreaPolicy - 'allow_warn' | 'block_on_detection'
 * @param {boolean} settings.attestationRequired
 * @param {boolean} settings.scanEnabled
 * @returns {Promise<void>}
 * @throws {Error} If write fails or validation fails
 */
export const savePhiPolicy = async (tenantId, updatedByUid, settings) => {
  const db = getDb();

  devLog('[savePhiPolicy] Saving for tenant:', tenantId);

  try {
    // Build and validate
    const policyDoc = buildPhiPolicy({
      tenantId,
      updatedByUid,
      pilotModeEnabled: settings.pilotModeEnabled,
      textAreaPolicy: settings.textAreaPolicy,
      attestationRequired: settings.attestationRequired,
      scanEnabled: settings.scanEnabled,
    });

    const validation = validatePhiPolicy(policyDoc);
    if (!validation.valid) {
      throw new Error(`PHI policy validation failed: ${validation.errors.join(', ')}`);
    }

    // Write to Firestore
    const policyRef = doc(db, PATHS.phiPolicy(tenantId));
    await setDoc(policyRef, policyDoc);

    devLog('[savePhiPolicy] ✅ Saved successfully');
  } catch (err) {
    console.error('[savePhiPolicy] ❌ Save failed:', err);
    throw new Error(`Failed to save PHI policy: ${err.message}`);
  }
};

/**
 * Log PHI warning without raw text (BLOCK 13 - Pilot Mode Runtime Safety)
 *
 * Logs PHI detection warnings to run session without storing raw text content
 * Stores only: fieldId, detection reasons, timestamp
 *
 * FIRESTORE WRITE:
 *   - tenants/{tenantId}/runSessions/{runId} (update with phiWarnings array)
 *
 * @param {string} tenantId - Tenant ID
 * @param {string} runId - Run session ID
 * @param {string} fieldId - Field ID where PHI was detected
 * @param {string[]} reasons - Detection reason codes (e.g., ['email_detected', 'phone_detected'])
 * @returns {Promise<void>}
 */
export const logPhiWarning = async (tenantId, runId, fieldId, reasons) => {
  const db = getDb();

  if (!tenantId || !runId || !fieldId || !Array.isArray(reasons)) {
    console.warn('[logPhiWarning] Invalid parameters, skipping log');
    return;
  }

  try {
    devLog('[logPhiWarning] Logging warning for field:', fieldId, 'reasons:', reasons);

    const runSessionRef = doc(db, PATHS.runSession(tenantId, runId));

    // Build warning entry (NO RAW TEXT)
    const warningEntry = {
      fieldId,
      reasons,
      detectedAt: toTimestamp(),
    };

    // Append to phiWarnings array
    await updateDoc(runSessionRef, {
      phiWarnings: arrayUnion(warningEntry),
      lastSeenAt: toTimestamp(),
    });

    devLog('[logPhiWarning] ✅ Warning logged successfully');
  } catch (err) {
    // Best-effort: don't block user if logging fails
    console.error('[logPhiWarning] ❌ Failed to log warning:', err);
  }
};

/**
 * Get PHI warnings for a run session (BLOCK 13)
 *
 * Retrieves PHI detection warnings for analysis/debugging
 * Returns structured warnings without raw text
 *
 * FIRESTORE READ:
 *   - tenants/{tenantId}/runSessions/{runId}
 *
 * @param {string} tenantId - Tenant ID
 * @param {string} runId - Run session ID
 * @returns {Promise<Array>} Array of warning objects { fieldId, reasons, detectedAt }
 */
export const getPhiWarnings = async (tenantId, runId) => {
  const db = getDb();

  devLog('[getPhiWarnings] Loading warnings for runId:', runId);

  try {
    const runSessionRef = doc(db, PATHS.runSession(tenantId, runId));
    const runSessionSnap = await getDoc(runSessionRef);

    if (runSessionSnap.exists()) {
      const data = runSessionSnap.data();
      const warnings = data.phiWarnings || [];
      devLog('[getPhiWarnings] ✅ Found warnings:', warnings.length);
      return warnings;
    } else {
      console.warn('[getPhiWarnings] ⚠️ Run session not found');
      return [];
    }
  } catch (err) {
    console.error('[getPhiWarnings] ❌ Read failed:', err);
    return [];
  }
};

// ============================================================================
// BLOCK 13: PHI WARNING DISMISSAL + AUDIT TRAIL
// ============================================================================

/**
 * Dismiss PHI warning with audit trail (BLOCK 13 - PHI Warning Dismissal)
 *
 * Records that a user consciously dismissed a PHI warning with required reason
 * Stores: signature (no raw text), dismissal reason enum, actor, timestamp
 *
 * FIRESTORE WRITE:
 *   - tenants/{tenantId}/phiDismissals/{dismissalId}
 *
 * INVARIANTS:
 *   - Must provide valid dismissalReason from PHI_DISMISSAL_REASONS enum
 *   - Must provide phiSignature generated from fieldId + reasons
 *   - NEVER stores raw answer text
 *   - Write must succeed for dismissal to be valid (fail-fast)
 *
 * @param {string} tenantId - Tenant ID
 * @param {string} runId - Run session ID
 * @param {string} fieldId - Field ID where PHI was detected
 * @param {string} phiSignature - PHI signature hash (from generatePhiSignature)
 * @param {string[]} detectionReasons - Detection reason codes
 * @param {string} dismissalReason - Dismissal reason enum value (required)
 * @param {Object} actor - User who dismissed { uid, role, tenantId }
 * @returns {Promise<string>} dismissalId
 * @throws {Error} If write fails (fail-fast required)
 */
export const dismissPhiWarning = async (
  tenantId,
  runId,
  fieldId,
  phiSignature,
  detectionReasons,
  dismissalReason,
  actor
) => {
  const db = getDb();

  if (!tenantId || !runId || !fieldId || !phiSignature || !dismissalReason || !actor) {
    throw new Error('[dismissPhiWarning] Missing required parameters');
  }

  // Validate dismissalReason is from allowed enum
  const validReasons = Object.values({
    CLINICAL_TIMELINE: 'clinical_timeline',
    DOSAGE_OR_LAB_VALUE: 'dosage_or_lab_value',
    CLINICAL_DATE_NOT_DOB: 'clinical_date_not_dob',
    MEASUREMENT_NOT_PHONE: 'measurement_not_phone',
    FALSE_POSITIVE_OTHER: 'false_positive_other',
  });

  if (!validReasons.includes(dismissalReason)) {
    throw new Error(`[dismissPhiWarning] Invalid dismissalReason: ${dismissalReason}`);
  }

  try {
    devLog('[dismissPhiWarning] Recording dismissal for signature:', phiSignature);

    // Generate unique dismissal ID
    const dismissalId = `${runId}_${phiSignature}_${Date.now()}`;

    const dismissalDoc = {
      tenantId,
      dismissalId,

      // Context (safe IDs only)
      runId,
      fieldId,
      phiSignature,                       // Hash of fieldId + detectionReasons (NO RAW TEXT)
      detectionReasons,                   // Array of reason codes (e.g., ['date_pattern', 'phone_detected'])

      // Dismissal metadata
      dismissalReason,                    // Enum: clinical_timeline, dosage_or_lab_value, etc.
      dismissedAt: toTimestamp(),

      // Actor audit
      dismissedByUid: actor.uid,
      dismissedByRole: actor.role,
      dismissedByTenantId: actor.tenantId || tenantId,
    };

    const dismissalRef = doc(db, PATHS.phiDismissal(tenantId, dismissalId));
    await setDoc(dismissalRef, dismissalDoc);

    devLog('[dismissPhiWarning] ✅ Dismissal recorded:', dismissalId);
    return dismissalId;
  } catch (err) {
    console.error('[dismissPhiWarning] ❌ Write failed:', err);
    throw new Error(`Failed to record PHI dismissal: ${err.message}`);
  }
};

/**
 * Check if PHI warning has been dismissed (BLOCK 13)
 *
 * Checks if a specific PHI warning signature has been dismissed for a run
 * Used to suppress repeated warnings for the same fieldId + reasons combination
 *
 * FIRESTORE READ:
 *   - tenants/{tenantId}/phiDismissals (query by runId + phiSignature)
 *
 * @param {string} tenantId - Tenant ID
 * @param {string} runId - Run session ID
 * @param {string} phiSignature - PHI signature hash
 * @returns {Promise<boolean>} True if dismissed
 */
export const isPhiWarningDismissed = async (tenantId, runId, phiSignature) => {
  const db = getDb();

  if (!tenantId || !runId || !phiSignature) {
    console.warn('[isPhiWarningDismissed] Invalid parameters');
    return false;
  }

  try {
    devLog('[isPhiWarningDismissed] Checking dismissal for:', phiSignature);

    const dismissalsRef = collection(db, PATHS.col.phiDismissals(tenantId));
    const q = query(
      dismissalsRef,
      where('runId', '==', runId),
      where('phiSignature', '==', phiSignature),
      limit(1)
    );

    const snapshot = await getDocs(q);
    const dismissed = !snapshot.empty;

    devLog('[isPhiWarningDismissed] Result:', dismissed);
    return dismissed;
  } catch (err) {
    console.error('[isPhiWarningDismissed] ❌ Query failed:', err);
    // Fail-open: if query fails, don't block user (assume not dismissed)
    return false;
  }
};

/**
 * Get PHI dismissals for a run (BLOCK 13)
 *
 * Retrieves all PHI dismissals for a specific run session
 * Used for debugging/audit purposes
 *
 * FIRESTORE READ:
 *   - tenants/{tenantId}/phiDismissals (query by runId)
 *
 * @param {string} tenantId - Tenant ID
 * @param {string} runId - Run session ID
 * @returns {Promise<Array>} Array of dismissal objects
 */
export const getPhiDismissalsForRun = async (tenantId, runId) => {
  const db = getDb();

  if (!tenantId || !runId) {
    console.warn('[getPhiDismissalsForRun] Invalid parameters');
    return [];
  }

  try {
    devLog('[getPhiDismissalsForRun] Loading dismissals for runId:', runId);

    const dismissalsRef = collection(db, PATHS.col.phiDismissals(tenantId));
    const q = query(
      dismissalsRef,
      where('runId', '==', runId),
      orderBy('dismissedAt', 'desc')
    );

    const snapshot = await getDocs(q);
    const dismissals = [];

    snapshot.forEach((docSnap) => {
      dismissals.push({
        dismissalId: docSnap.id,
        ...docSnap.data(),
      });
    });

    devLog('[getPhiDismissalsForRun] ✅ Found dismissals:', dismissals.length);
    return dismissals;
  } catch (err) {
    console.error('[getPhiDismissalsForRun] ❌ Query failed:', err);
    return [];
  }
};

// ============================================================================
// BLOCK 6: TENANT ANALYTICS DASHBOARD
// ============================================================================

/**
 * Get tenant analytics dashboard data (BLOCK 6)
 *
 * Returns adoption, behavioral impact, and value metrics for a tenant-scoped dashboard
 * All metrics are computed from tenants/{tenantId}/metrics (no UI-state analytics)
 *
 * FIRESTORE READ:
 *   - tenants/{tenantId}/metrics (query by savedAt, optionally filtered)
 *   - tenants/{tenantId}/settings/impact (for minutesSavedPerPreventedReferral)
 *
 * @param {string} tenantId - Tenant ID
 * @param {Object} filters - Optional filters { specialtyId, packId, startDate, endDate, daysBack }
 * @returns {Promise<Object>} Analytics data object
 */
export const getTenantAnalytics = async (tenantId, filters = {}) => {
  const db = getDb();
  const { specialtyId = null, packId = null, daysBack = 30 } = filters;

  devLog('[getTenantAnalytics] Loading for tenant:', tenantId, 'filters:', filters);

  try {
    // Compute date range
    const now = new Date();
    const startDate = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));
    const startTimestamp = Timestamp.fromDate(startDate);

    // Query metrics in date range
    const metricsRef = collection(db, PATHS.col.metrics(tenantId));

    let constraints = [
      where('savedAt', '>=', startTimestamp),
      orderBy('savedAt', 'desc'),
    ];

    if (specialtyId) {
      constraints = [
        where('specialtyId', '==', specialtyId),
        where('savedAt', '>=', startTimestamp),
        orderBy('savedAt', 'desc'),
      ];
    }

    if (packId) {
      constraints = [
        where('packId', '==', packId),
        where('savedAt', '>=', startTimestamp),
        orderBy('savedAt', 'desc'),
      ];
    }

    const q = query(metricsRef, ...constraints);
    const snapshot = await getDocs(q);

    // Fetch impact settings for value calculation
    let impactSettings = null;
    let minutesSavedPerPreventedReferral = 5; // default
    try {
      impactSettings = await getImpactSettings(tenantId);
      minutesSavedPerPreventedReferral = impactSettings.assumptions?.minutesSavedPerPreventedReferral || 5;
    } catch (err) {
      console.warn('[getTenantAnalytics] Using default minutesSaved:', err);
    }

    // Aggregate metrics
    const uniqueUsers = new Set();
    let totalRuns = 0;
    let routineCount = 0;
    let urgentCount = 0;
    let notYetCount = 0;
    let completedCount = 0;
    let overrideCount = 0;
    let overrideFinalizedCount = 0; // overrides on finalized (routine/urgent) outcomes only
    let pdfGeneratedCount = 0;
    let reclaimedMinutes = 0;

    const bySpecialty = {};
    const byPack = {};
    const dailyCounts = {};

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      totalRuns++;

      // Track unique users
      if (data.createdByUid) {
        uniqueUsers.add(data.createdByUid);
      }

      // Outcome counts
      const status = normalizeOutcomeStatus(data.outcomeStatus);
      if (status === 'routine') routineCount++;
      else if (status === 'urgent') urgentCount++;
      else if (status === 'not_yet') notYetCount++;

      if (status === 'routine' || status === 'urgent') {
        completedCount++;
      }

      // Tracking flags
      if (data.overrideUsed) {
        overrideCount++;
        if (status === 'routine' || status === 'urgent') {
          overrideFinalizedCount++;
        }
      }
      if (data.pdfGenerated) pdfGeneratedCount++;

      // Value calculation (reclaimed minutes)
      if (data.computed?.estimatedMinutesReclaimed) {
        reclaimedMinutes += data.computed.estimatedMinutesReclaimed;
      } else if (status === 'not_yet') {
        // Fallback if computed field missing
        reclaimedMinutes += minutesSavedPerPreventedReferral;
      }

      // Breakdown by specialty
      const spec = data.specialtyId || 'unknown';
      if (!bySpecialty[spec]) {
        bySpecialty[spec] = { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0 };
      }
      bySpecialty[spec].totalRuns++;
      if (status === 'routine') bySpecialty[spec].routine++;
      else if (status === 'urgent') bySpecialty[spec].urgent++;
      else if (status === 'not_yet') bySpecialty[spec].not_yet++;

      // Breakdown by pack
      const pack = data.packId || 'unknown';
      if (!byPack[pack]) {
        byPack[pack] = { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0 };
      }
      byPack[pack].totalRuns++;
      if (status === 'routine') byPack[pack].routine++;
      else if (status === 'urgent') byPack[pack].urgent++;
      else if (status === 'not_yet') byPack[pack].not_yet++;

      // Daily time series
      if (data.savedAt) {
        const date = data.savedAt.toDate();
        const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
        if (!dailyCounts[dateKey]) {
          dailyCounts[dateKey] = { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0 };
        }
        dailyCounts[dateKey].totalRuns++;
        if (status === 'routine') dailyCounts[dateKey].routine++;
        else if (status === 'urgent') dailyCounts[dateKey].urgent++;
        else if (status === 'not_yet') dailyCounts[dateKey].not_yet++;
      }
    });

    // Calculate rates
    const completedRate = totalRuns > 0 ? Math.round((completedCount / totalRuns) * 100) : 0;
    const notYetRate = totalRuns > 0 ? Math.round((notYetCount / totalRuns) * 100) : 0;
    const urgentRate = totalRuns > 0 ? Math.round((urgentCount / totalRuns) * 100) : 0;
    const overrideRate = totalRuns > 0 ? Math.round((overrideCount / totalRuns) * 100) : 0;
    const incompleteInterceptRate = notYetRate; // Same as not_yet_rate

    // Calculate active users last 7 days
    const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
    const sevenDaysTimestamp = Timestamp.fromDate(sevenDaysAgo);
    const activeUsers7d = new Set();

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      if (data.savedAt && data.savedAt.toMillis() >= sevenDaysTimestamp.toMillis()) {
        if (data.createdByUid) {
          activeUsers7d.add(data.createdByUid);
        }
      }
    });

    const result = {
      // Adoption metrics
      adoption: {
        totalRuns,
        activeUsers7d: activeUsers7d.size,
        totalUniqueUsers: uniqueUsers.size,
        runsToday: dailyCounts[new Date().toISOString().split('T')[0]]?.totalRuns || 0,
      },

      // Behavioral impact metrics
      impact: {
        completedRate,
        notYetRate,
        urgentRate,
        overrideRate,
        incompleteInterceptRate,
        counts: {
          routine: routineCount,
          urgent: urgentCount,
          not_yet: notYetCount,
          completed: completedCount,
          overrides: overrideCount,
          overridesFinalized: overrideFinalizedCount,
          pdfGenerated: pdfGeneratedCount,
        },
      },

      // Value metrics
      value: {
        reclaimedMinutes,
        reclaimedHours: Math.round((reclaimedMinutes / 60) * 10) / 10, // 1 decimal
        minutesSavedPerPreventedReferral,
        isUsingDefaultSettings: !impactSettings || impactSettings === DEFAULT_IMPACT_SETTINGS,
      },

      // Breakdowns
      bySpecialty,
      byPack,

      // Time series
      dailyCounts,

      // Metadata
      filters: {
        tenantId,
        specialtyId,
        packId,
        daysBack,
        startDate: startDate.toISOString(),
      },
    };

    devLog('[getTenantAnalytics] ✅ Computed analytics:', result);

    return result;
  } catch (err) {
    console.error('[getTenantAnalytics] ❌ Query failed:', err);
    throw new Error(`Failed to load tenant analytics: ${err.message}`);
  }
};

// ============================================================================
// BLOCK 10: RUN SESSION TELEMETRY (Funnel: Started → Submitted)
// ============================================================================

/**
 * Start a new run session (BLOCK 10)
 *
 * Creates a RunSessionDoc when user selects a pathway and begins a run
 * Idempotent: if session already exists, updates lastSeenAt + lastScreen
 *
 * FIRESTORE WRITE:
 *   - tenants/{tenantId}/runSessions/{runId}
 *
 * @param {Object} params
 * @returns {Promise<{runId: string}>}
 */
export const startRunSession = async ({
  tenantId,
  runId = null,
  specialtyId,
  packId,
  versionId,
  createdByUid,
  lastScreen = 1,
}) => {
  const db = getDb();

  const sessionId = runId || generateRunSessionId();

  devLog('[startRunSession] Starting session:', sessionId);

  try {
    // PERMISSION DEBUG: Fetch and verify user document (diagnose PERMISSION_DENIED)
    try {
      const userRef = doc(db, PATHS.user(createdByUid));
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        devLog('[startRunSession] DEBUG: User role:', userData.role || 'NOT_SET');
        devLog('[startRunSession] DEBUG: User tenantId:', userData.tenantId || 'NOT_SET');
        devLog('[startRunSession] DEBUG: Expected tenantId:', tenantId);

        // Check for mismatch
        if (userData.tenantId !== tenantId) {
          console.error('[startRunSession] ❌ TENANT MISMATCH! User doc has tenantId:', userData.tenantId, 'but trying to access tenant:', tenantId);
          console.error('[startRunSession] ❌ This will cause PERMISSION_DENIED. Fix user document in Firestore.');
        }
        if (!['gp_employee', 'specialist'].includes(userData.role)) {
          console.warn('[startRunSession] ⚠️  User role is not a clinician:', userData.role);
        }
      } else {
        console.error('[startRunSession] ❌ User document NOT FOUND for UID:', createdByUid);
      }
    } catch (userFetchErr) {
      console.warn('[startRunSession] Could not fetch user document for debugging:', userFetchErr.message);
    }

    const sessionRef = doc(db, PATHS.runSession(tenantId, sessionId));

    // Check if session already exists
    const existingSession = await getDoc(sessionRef);

    if (existingSession.exists()) {
      // Update existing session (idempotent)
      devLog('[startRunSession] Session exists, updating lastSeenAt');
      await updateDoc(sessionRef, {
        lastSeenAt: toTimestamp(),
        lastScreen: typeof lastScreen === 'number' ? lastScreen : 1,
      });
    } else {
      // Create new session
      const sessionDoc = buildRunSession({
        tenantId,
        runId: sessionId,
        specialtyId,
        packId,
        versionId,
        createdByUid,
        state: 'started',
        lastScreen,
      });

      const validation = validateRunSession(sessionDoc);
      if (!validation.valid) {
        throw new Error(`Run session validation failed: ${validation.errors.join(', ')}`);
      }

      // PERMISSION DEBUG LOGGING
      devLog('[startRunSession] WRITE operation: CREATE');
      devLog('[startRunSession] Path:', `tenants/${tenantId}/runSessions/${sessionId}`);
      devLog('[startRunSession] Payload keys:', Object.keys(sessionDoc));
      devLog('[startRunSession] Payload sample:', {
        tenantId: sessionDoc.tenantId,
        createdByUid: sessionDoc.createdByUid,
        specialtyId: sessionDoc.specialtyId,
        packId: sessionDoc.packId,
        versionId: sessionDoc.versionId,
        runId: sessionDoc.runId,
      });

      await setDoc(sessionRef, sessionDoc);
      devLog('[startRunSession] ✅ Session created');
    }

    return { runId: sessionId };
  } catch (err) {
    console.error('[startRunSession] ❌ Failed:', err);
    // Best-effort: don't block user if telemetry fails
    return { runId: sessionId };
  }
};

/**
 * Update run session progress (BLOCK 10)
 *
 * Updates heartbeat + progress tracking (non-blocking, best-effort)
 *
 * FIRESTORE WRITE:
 *   - tenants/{tenantId}/runSessions/{runId} (update)
 *
 * @param {string} tenantId
 * @param {string} runId
 * @param {Object} updates - { state, lastScreen, completionPct, missingRequiredCount }
 * @returns {Promise<void>}
 */
export const updateRunSessionProgress = async (tenantId, runId, updates = {}) => {
  const db = getDb();

  devLog('[updateRunSessionProgress] Updating session:', runId, updates);

  try {
    const sessionRef = doc(db, PATHS.runSession(tenantId, runId));

    const updateData = {
      lastSeenAt: toTimestamp(),
      ...updates,
    };

    // Ensure state is valid if provided
    if (updates.state && !['started', 'in_progress', 'submitted', 'abandoned'].includes(updates.state)) {
      console.warn('[updateRunSessionProgress] Invalid state:', updates.state);
      delete updateData.state;
    }

    // PERMISSION DEBUG LOGGING
    devLog('[updateRunSessionProgress] WRITE operation: UPDATE');
    devLog('[updateRunSessionProgress] Path:', `tenants/${tenantId}/runSessions/${runId}`);
    devLog('[updateRunSessionProgress] Payload keys:', Object.keys(updateData));
    devLog('[updateRunSessionProgress] Payload sample:', updateData);

    await updateDoc(sessionRef, updateData);
    devLog('[updateRunSessionProgress] ✅ Updated');
  } catch (err) {
    // Best-effort: don't block user if telemetry fails
    console.warn('[updateRunSessionProgress] ⚠️  Update failed (non-blocking):', err);
  }
};

/**
 * Mark run session as submitted (BLOCK 10)
 *
 * Called when submitReferral succeeds
 *
 * FIRESTORE WRITE:
 *   - tenants/{tenantId}/runSessions/{runId} (update)
 *
 * @param {string} tenantId
 * @param {string} runId
 * @param {string} referralId
 * @returns {Promise<void>}
 */
export const markRunSessionSubmitted = async (tenantId, runId, referralId) => {
  const db = getDb();

  devLog('[markRunSessionSubmitted] Marking submitted:', runId);

  try {
    const sessionRef = doc(db, PATHS.runSession(tenantId, runId));

    const updateData = {
      state: 'submitted',
      submittedAt: toTimestamp(),
      lastSeenAt: toTimestamp(),
      referralId,
    };

    // PERMISSION DEBUG LOGGING
    devLog('[markRunSessionSubmitted] WRITE operation: UPDATE');
    devLog('[markRunSessionSubmitted] Path:', `tenants/${tenantId}/runSessions/${runId}`);
    devLog('[markRunSessionSubmitted] Payload keys:', Object.keys(updateData));
    devLog('[markRunSessionSubmitted] Payload sample:', updateData);

    await updateDoc(sessionRef, updateData);

    devLog('[markRunSessionSubmitted] ✅ Marked submitted');
  } catch (err) {
    // Best-effort: don't block submission if telemetry fails
    console.warn('[markRunSessionSubmitted] ⚠️  Update failed (non-blocking):', err);
  }
};

/**
 * Mark run session as abandoned (BLOCK 10)
 *
 * Called on timeout or beforeunload (best-effort)
 *
 * FIRESTORE WRITE:
 *   - tenants/{tenantId}/runSessions/{runId} (update)
 *
 * @param {string} tenantId
 * @param {string} runId
 * @param {number} lastScreen
 * @returns {Promise<void>}
 */
export const markRunSessionAbandoned = async (tenantId, runId, lastScreen = 1) => {
  const db = getDb();

  devLog('[markRunSessionAbandoned] Marking abandoned:', runId);

  try {
    const sessionRef = doc(db, PATHS.runSession(tenantId, runId));

    // Check current state - don't overwrite submitted
    const sessionSnap = await getDoc(sessionRef);
    if (sessionSnap.exists()) {
      const currentState = sessionSnap.data().state;
      if (currentState === 'submitted') {
        devLog('[markRunSessionAbandoned] Already submitted, skipping');
        return;
      }
    }

    await updateDoc(sessionRef, {
      state: 'abandoned',
      abandonedAt: toTimestamp(),
      lastSeenAt: toTimestamp(),
      lastScreen,
    });

    devLog('[markRunSessionAbandoned] ✅ Marked abandoned');
  } catch (err) {
    // Best-effort: don't block anything if telemetry fails
    console.warn('[markRunSessionAbandoned] ⚠️  Update failed (non-blocking):', err);
  }
};

// ============================================================================
// OVERRIDE PERSISTENCE (Decision Override - Secondary Escape Hatch)
// ============================================================================

/**
 * Save override to run session (REQUIRED at override confirm time)
 *
 * When a GP clicks "Override gate" on the Decision screen and confirms,
 * this function writes the override record to the runSession IMMEDIATELY.
 * The override is then copied to the MetricEventDoc during final submit.
 *
 * FIRESTORE WRITE:
 *   - tenants/{tenantId}/runSessions/{runId} (UPDATE: set override fields)
 *
 * @param {string} tenantId - Tenant ID (required)
 * @param {string} runId - Run session ID (required)
 * @param {Object} override - Override record (required)
 * @param {string} override.reason - Override reason code (required)
 * @param {string|null} override.note - Optional note (max 240 chars, non-PHI)
 * @param {string} override.confirmedByUid - User ID who confirmed (required)
 * @param {string} override.confirmedByRole - User role (required)
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const saveOverrideToRunSession = async (tenantId, runId, override) => {
  const db = getDb();

  devLog('[saveOverrideToRunSession] Saving override to runSession:', runId);
  devLog('[saveOverrideToRunSession] Override reason:', override?.reason);

  // Validate required parameters
  if (!tenantId) {
    const error = 'saveOverrideToRunSession: tenantId is required';
    console.error(error);
    return { success: false, error };
  }

  if (!runId) {
    const error = 'saveOverrideToRunSession: runId is required';
    console.error(error);
    return { success: false, error };
  }

  if (!override || !override.reason) {
    const error = 'saveOverrideToRunSession: override.reason is required';
    console.error(error);
    return { success: false, error };
  }

  if (!override.confirmedByUid) {
    const error = 'saveOverrideToRunSession: override.confirmedByUid is required';
    console.error(error);
    return { success: false, error };
  }

  if (!override.confirmedByRole) {
    const error = 'saveOverrideToRunSession: override.confirmedByRole is required';
    console.error(error);
    return { success: false, error };
  }

  try {
    const sessionRef = doc(db, PATHS.runSession(tenantId, runId));

    // Prepare update data
    const updateData = {
      // Full override object
      override: {
        used: true,
        reason: override.reason,
        note: override.note || null,
        confirmedByUid: override.confirmedByUid,
        confirmedByRole: override.confirmedByRole,
        confirmedAt: toTimestamp(),
      },

      // Shorthand fields for easy querying/filtering
      overrideUsed: true,
      overrideReason: override.reason,
      overrideNote: override.note || null,
      overrideConfirmedByUid: override.confirmedByUid,
      overrideConfirmedAt: toTimestamp(),

      // Update lastSeenAt
      lastSeenAt: toTimestamp(),
      updatedAt: toTimestamp(),
    };

    // PERMISSION DEBUG LOGGING
    devLog('[saveOverrideToRunSession] WRITE operation: UPDATE');
    devLog('[saveOverrideToRunSession] Path:', `tenants/${tenantId}/runSessions/${runId}`);
    devLog('[saveOverrideToRunSession] Payload keys:', Object.keys(updateData));

    await updateDoc(sessionRef, updateData);

    devLog('[saveOverrideToRunSession] ✅ Override saved successfully');
    return { success: true };
  } catch (err) {
    console.error('[saveOverrideToRunSession] ❌ Failed to save override:', err);
    return {
      success: false,
      error: err.message || 'Failed to save override',
      code: err.code,
    };
  }
};

/**
 * Get override from run session (read for copying to metrics)
 *
 * FIRESTORE READ:
 *   - tenants/{tenantId}/runSessions/{runId}
 *
 * @param {string} tenantId - Tenant ID
 * @param {string} runId - Run session ID
 * @returns {Promise<Object|null>} Override object or null if not present
 */
export const getOverrideFromRunSession = async (tenantId, runId) => {
  const db = getDb();

  devLog('[getOverrideFromRunSession] Reading override from runSession:', runId);

  if (!tenantId || !runId) {
    console.warn('[getOverrideFromRunSession] Missing tenantId or runId');
    return null;
  }

  try {
    const sessionRef = doc(db, PATHS.runSession(tenantId, runId));
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists()) {
      devLog('[getOverrideFromRunSession] Run session not found');
      return null;
    }

    const data = sessionSnap.data();

    if (data.override && data.override.used) {
      devLog('[getOverrideFromRunSession] ✅ Found override:', data.override.reason);
      return data.override;
    }

    devLog('[getOverrideFromRunSession] No override present');
    return null;
  } catch (err) {
    console.warn('[getOverrideFromRunSession] ⚠️  Failed to read override:', err);
    return null;
  }
};

/**
 * Get funnel analytics (started vs completed) (BLOCK 10)
 *
 * Computes completion_rate_funnel from runSessions
 *
 * FIRESTORE READ:
 *   - tenants/{tenantId}/runSessions (query by startedAt)
 *
 * @param {string} tenantId
 * @param {Object} filters - { specialtyId, packId, daysBack }
 * @returns {Promise<Object>} Funnel metrics
 */
export const getRunSessionFunnelMetrics = async (tenantId, filters = {}) => {
  const db = getDb();
  const { specialtyId = null, packId = null, daysBack = 30 } = filters;

  devLog('[getRunSessionFunnelMetrics] Loading for tenant:', tenantId);

  try {
    const now = new Date();
    const startDate = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));
    const startTimestamp = Timestamp.fromDate(startDate);

    const sessionsRef = collection(db, PATHS.col.runSessions(tenantId));

    let constraints = [
      where('startedAt', '>=', startTimestamp),
      orderBy('startedAt', 'desc'),
    ];

    if (specialtyId) {
      constraints = [
        where('specialtyId', '==', specialtyId),
        where('startedAt', '>=', startTimestamp),
        orderBy('startedAt', 'desc'),
      ];
    }

    if (packId) {
      constraints = [
        where('packId', '==', packId),
        where('startedAt', '>=', startTimestamp),
        orderBy('startedAt', 'desc'),
      ];
    }

    const q = query(sessionsRef, ...constraints);
    const snapshot = await getDocs(q);

    let startedRuns = 0;
    let submittedRuns = 0;
    let abandonedRuns = 0;
    const dropoffByScreen = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      startedRuns++;

      if (data.state === 'submitted') {
        submittedRuns++;
      } else if (data.state === 'abandoned') {
        abandonedRuns++;
        const screen = data.lastScreen || 1;
        if (screen >= 1 && screen <= 5) {
          dropoffByScreen[screen]++;
        }
      }
    });

    const completionRate = startedRuns > 0
      ? Math.round((submittedRuns / startedRuns) * 100)
      : 0;

    const abandonmentRate = startedRuns > 0
      ? Math.round((abandonedRuns / startedRuns) * 100)
      : 0;

    const result = {
      startedRuns,
      submittedRuns,
      abandonedRuns,
      completionRate,
      abandonmentRate,
      dropoffByScreen,
    };

    devLog('[getRunSessionFunnelMetrics] ✅ Results:', result);

    return result;
  } catch (err) {
    console.error('[getRunSessionFunnelMetrics] ❌ Query failed:', err);
    throw new Error(`Failed to load funnel metrics: ${err.message}`);
  }
};

// ============================================================================
// BLOCK 12: DAILY ROLLUP BACKFILL UTILITY
// ============================================================================

/**
 * Backfill daily rollups for a tenant + date range (BLOCK 12)
 *
 * Scans metrics events and rebuilds rollup documents for each day
 * Idempotent: safe to re-run (uses setDoc with merge)
 *
 * FIRESTORE OPERATIONS:
 *   - READ: tenants/{tenantId}/metrics/{eventId} (all metrics in date range)
 *   - READ: tenants/{tenantId}/settings/impact (for minutesSavedPerPreventedReferral)
 *   - WRITE: tenants/{tenantId}/rollups/daily/{dateId} (set with merge for each day)
 *
 * @param {string} tenantId - Tenant ID
 * @param {string} startDateId - Start date in YYYY-MM-DD format (inclusive)
 * @param {string} endDateId - End date in YYYY-MM-DD format (inclusive)
 * @returns {Promise<Object>} { success: boolean, daysProcessed: number, totalMetrics: number }
 */
export const backfillDailyRollups = async (tenantId, startDateId, endDateId) => {
  if (!tenantId) throw new Error('backfillDailyRollups: tenantId is required');
  if (!startDateId) throw new Error('backfillDailyRollups: startDateId is required (YYYY-MM-DD)');
  if (!endDateId) throw new Error('backfillDailyRollups: endDateId is required (YYYY-MM-DD)');

  devLog(`[backfillDailyRollups] Starting backfill for tenant ${tenantId} from ${startDateId} to ${endDateId}`);

  const db = getDb();

  // Fetch impact settings
  let minutesSavedPerPreventedReferral = 5; // default
  try {
    const impactSettings = await getImpactSettings(tenantId);
    minutesSavedPerPreventedReferral = impactSettings?.assumptions?.minutesSavedPerPreventedReferral || 5;
  } catch (err) {
    console.warn('[backfillDailyRollups] Could not load impact settings, using default (5 min):', err);
  }

  // Convert date strings to Timestamp range
  const startDate = new Date(`${startDateId}T00:00:00.000Z`);
  const endDate = new Date(`${endDateId}T23:59:59.999Z`);
  const startTimestamp = Timestamp.fromDate(startDate);
  const endTimestamp = Timestamp.fromDate(endDate);

  // Query all metrics in date range
  const metricsRef = collection(db, PATHS.col.metrics(tenantId));
  const q = query(
    metricsRef,
    where('savedAt', '>=', startTimestamp),
    where('savedAt', '<=', endTimestamp),
    orderBy('savedAt', 'asc')
  );

  devLog('[backfillDailyRollups] Querying metrics...');

  let snapshot;
  try {
    snapshot = await getDocs(q);
  } catch (err) {
    console.error('[backfillDailyRollups] ❌ Query failed:', err);
    throw new Error(`Failed to query metrics: ${err.message}`);
  }

  if (snapshot.empty) {
    devLog('[backfillDailyRollups] No metrics found in date range');
    return {
      success: true,
      daysProcessed: 0,
      totalMetrics: 0,
    };
  }

  devLog(`[backfillDailyRollups] Found ${snapshot.size} metrics to process`);

  // Group metrics by dateId
  const metricsByDate = {};
  snapshot.forEach((docSnap) => {
    const metric = docSnap.data();
    const dateId = generateDateId(metric.savedAt);

    if (!metricsByDate[dateId]) {
      metricsByDate[dateId] = [];
    }

    metricsByDate[dateId].push(metric);
  });

  const daysProcessed = Object.keys(metricsByDate).length;
  devLog(`[backfillDailyRollups] Grouped into ${daysProcessed} days`);

  // Process each day
  const writePromises = [];

  for (const [dateId, metrics] of Object.entries(metricsByDate)) {
    devLog(`[backfillDailyRollups] Processing ${dateId} with ${metrics.length} metrics`);

    // Build rollup document from scratch
    const counts = {
      totalRuns: 0,
      routine: 0,
      urgent: 0,
      not_yet: 0,
      completed: 0,
      overrides: 0,
      pdfGenerated: 0,
    };

    let reclaimedMinutes = 0;
    const bySpecialty = {};
    const byPack = {};

    metrics.forEach((metric) => {
      const outcomeStatus = normalizeOutcomeStatus(metric.outcomeStatus);

      // Base counts
      counts.totalRuns++;
      counts[outcomeStatus]++;

      // Completed count
      if (outcomeStatus === 'routine' || outcomeStatus === 'urgent') {
        counts.completed++;
      }

      // Overrides
      if (metric.overrideUsed === true) {
        counts.overrides++;
      }

      // PDF generated
      if (metric.pdfGenerated === true) {
        counts.pdfGenerated++;
      }

      // Reclaimed minutes
      if (outcomeStatus === 'not_yet') {
        reclaimedMinutes += minutesSavedPerPreventedReferral;
      }

      // Breakdown by specialty (rich gate + capacity sub-objects)
      if (metric.specialtyId) {
        if (!bySpecialty[metric.specialtyId]) {
          bySpecialty[metric.specialtyId] = {
            gate: { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0, completed: 0, overrides: 0, created: 0 },
            capacity: { reclaimedMinutes: 0 },
          };
        }
        const sg = bySpecialty[metric.specialtyId].gate;
        sg.totalRuns++;
        sg[outcomeStatus] = (sg[outcomeStatus] || 0) + 1;
        if (outcomeStatus === 'routine' || outcomeStatus === 'urgent') sg.completed++;
        if (metric.overrideUsed === true) sg.overrides++;
        if (metric.isFirstRun !== false) sg.created++;
        if (outcomeStatus === 'not_yet') {
          bySpecialty[metric.specialtyId].capacity.reclaimedMinutes += minutesSavedPerPreventedReferral;
        }
      }

      // Breakdown by pack
      if (metric.packId) {
        if (!byPack[metric.packId]) {
          byPack[metric.packId] = { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0 };
        }
        byPack[metric.packId].totalRuns++;
        byPack[metric.packId][outcomeStatus]++;
      }
    });

    // Build rollup doc
    const rollupDoc = buildDailyRollup({
      tenantId,
      dateId,
      counts,
      value: { reclaimedMinutes },
      breakdowns: { bySpecialty, byPack },
    });

    // Write rollup (merge to be idempotent)
    // Path: tenants/{tenantId}/rollups/daily/dates/{dateId} (6 segments for valid Firestore doc ref)
    const rollupRef = doc(db, PATHS.rollupDaily(tenantId, dateId));
    const writePromise = setDoc(rollupRef, rollupDoc, { merge: true })
      .then(() => {
        devLog(`[backfillDailyRollups] ✅ Wrote rollup for ${dateId}`);
      })
      .catch((err) => {
        console.error(`[backfillDailyRollups] ❌ Failed to write rollup for ${dateId}:`, err);
        throw err;
      });

    writePromises.push(writePromise);
  }

  // Wait for all writes to complete
  try {
    await Promise.all(writePromises);
    devLog(`[backfillDailyRollups] ✅ Backfill complete: ${daysProcessed} days, ${snapshot.size} metrics`);

    return {
      success: true,
      daysProcessed,
      totalMetrics: snapshot.size,
    };
  } catch (err) {
    console.error('[backfillDailyRollups] ❌ Backfill failed:', err);
    throw new Error(`Backfill failed: ${err.message}`);
  }
};

/**
 * Get platform analytics from daily rollups (BLOCK 12)
 *
 * Fast aggregation by reading pre-computed rollup docs instead of scanning all metrics
 * Supports cross-tenant or per-tenant queries
 *
 * FIRESTORE OPERATIONS:
 *   - READ: tenants/{tenantId}/rollups/daily/{dateId} (for all days in range)
 *
 * @param {Object} filters - Query filters
 * @param {string|null} filters.tenantId - Tenant ID (null for all tenants, platform admin only)
 * @param {string} filters.startDateId - Start date YYYY-MM-DD (inclusive)
 * @param {string} filters.endDateId - End date YYYY-MM-DD (inclusive)
 * @returns {Promise<Object>} Aggregated analytics
 */
export const getPlatformAnalyticsFromRollups = async (filters = {}) => {
  const { tenantId = null, startDateId, endDateId } = filters;

  if (!startDateId) throw new Error('getPlatformAnalyticsFromRollups: startDateId is required (YYYY-MM-DD)');
  if (!endDateId) throw new Error('getPlatformAnalyticsFromRollups: endDateId is required (YYYY-MM-DD)');

  devLog(`[getPlatformAnalyticsFromRollups] Loading rollups from ${startDateId} to ${endDateId}`, tenantId ? `for tenant ${tenantId}` : '(all tenants)');

  const db = getDb();

  // Generate list of all date IDs in range (using UTC to match Cloud Function)
  const startDate = new Date(`${startDateId}T00:00:00.000Z`);
  const endDate = new Date(`${endDateId}T00:00:00.000Z`);
  const dateIds = [];
  for (let d = new Date(startDate); d <= endDate; d.setUTCDate(d.getUTCDate() + 1)) {
    dateIds.push(generateDateId(d));
  }

  devLog(`[getPlatformAnalyticsFromRollups] Will read ${dateIds.length} days of rollups`);

  // For single tenant: read rollups directly
  // Path: tenants/{tenantId}/rollups/daily/dates/{dateId}
  // Structure: tenants (coll) -> tenantId (doc) -> rollups (coll) -> daily (doc) -> dates (coll) -> dateId (doc)
  if (tenantId) {
    devLog(`[getPlatformAnalyticsFromRollups] Querying rollups for dateIds:`, dateIds);

    const rollupPromises = dateIds.map(async (dateId) => {
      // 6 segments: tenants/{tenantId}/rollups/daily/dates/{dateId}
      const rollupRef = doc(db, PATHS.rollupDaily(tenantId, dateId));
      const fullPath = `tenants/${tenantId}/rollups/daily/dates/${dateId}`;
      devLog(`[getPlatformAnalyticsFromRollups] Reading doc at path: ${fullPath}`);

      const snapshot = await getDoc(rollupRef);
      devLog(`[getPlatformAnalyticsFromRollups] Snapshot for ${dateId}: exists=${snapshot.exists()}, data=`, snapshot.exists() ? snapshot.data() : null);

      if (snapshot.exists()) {
        return { dateId, ...snapshot.data() };
      }
      return null;
    });

    const rollups = (await Promise.all(rollupPromises)).filter(r => r !== null);

    devLog(`[getPlatformAnalyticsFromRollups] Found ${rollups.length} rollup docs for tenant ${tenantId}`);
    rollups.forEach((r, i) => {
      devLog(`[getPlatformAnalyticsFromRollups] Rollup[${i}] dateId=${r.dateId}, counts=`, r.counts, 'value=', r.value);
    });

    // Pass totalDaysInRange for coverage calculation
    return aggregateRollups(rollups, dateIds.length);
  }

  // For all tenants (platform admin): read from all tenants (requires listing tenants first)
  // This is more complex and may require a different data model (e.g., global rollups collection)
  // For MVP, throw an error and require tenantId
  throw new Error('getPlatformAnalyticsFromRollups: cross-tenant queries not yet implemented. Please specify tenantId.');
};

/**
 * getPlatformCrossTenantAnalytics - Read cross-tenant analytics
 *
 * Strategy:
 * 1. Try platform_metrics/days/items/{dateId} (Cloud Function rollups)
 * 2. If no data found, fallback to reading each tenant's existing rollups
 *    at tenants/{tenantId}/rollups/daily/dates/{dateId} and aggregating
 *
 * @param {Object} params
 * @param {string} params.startDateId - Start date YYYY-MM-DD (inclusive)
 * @param {string} params.endDateId - End date YYYY-MM-DD (inclusive)
 * @param {string[]} [params.tenantIds] - Optional tenant IDs for fallback reads
 * @returns {Promise<Object>} Cross-tenant analytics
 */
export const getPlatformCrossTenantAnalytics = async ({ startDateId, endDateId, tenantIds = [] }) => {
  if (!startDateId || !endDateId) {
    throw new Error('getPlatformCrossTenantAnalytics: startDateId and endDateId are required');
  }

  devLog(`[getPlatformCrossTenantAnalytics] Loading from ${startDateId} to ${endDateId}, tenantIds:`, tenantIds.length);

  const db = getDb();

  // Generate list of all date IDs in range
  const startDate = new Date(`${startDateId}T00:00:00.000Z`);
  const endDate = new Date(`${endDateId}T00:00:00.000Z`);
  const dateIds = [];
  for (let d = new Date(startDate); d <= endDate; d.setUTCDate(d.getUTCDate() + 1)) {
    dateIds.push(generateDateId(d));
  }

  // ========================================================================
  // 1. Try platform_metrics first (Cloud Function rollups)
  // ========================================================================
  let usedPlatformMetrics = false;
  let platformDays = [];

  try {
    const platformDayPromises = dateIds.map(async (dateId) => {
      const ref = doc(db, PATHS.platformMetricsDay(dateId));
      try {
        const snap = await getDoc(ref);
        if (snap.exists()) return { dateId, ...snap.data() };
        return null;
      } catch { return null; }
    });
    platformDays = (await Promise.all(platformDayPromises)).filter(Boolean);
  } catch (err) {
    console.warn('[getPlatformCrossTenantAnalytics] platform_metrics read failed:', err.message);
  }

  devLog(`[getPlatformCrossTenantAnalytics] platform_metrics days found: ${platformDays.length}`);

  // ========================================================================
  // 2. Fallback: read existing tenant rollups if platform_metrics is empty
  // ========================================================================
  // Per-tenant rollup data: array of { tenantId, dateId, rollupData }
  let perTenantRollups = [];

  if (platformDays.length === 0 && tenantIds.length > 0) {
    devLog(`[getPlatformCrossTenantAnalytics] Falling back to tenant rollups for ${tenantIds.length} tenants`);

    const tenantRollupPromises = tenantIds.map(async (tenantId) => {
      const dayPromises = dateIds.map(async (dateId) => {
        const ref = doc(db, PATHS.rollupDaily(tenantId, dateId));
        try {
          const snap = await getDoc(ref);
          if (snap.exists()) return { tenantId, dateId, ...snap.data() };
          return null;
        } catch { return null; }
      });
      return (await Promise.all(dayPromises)).filter(Boolean);
    });

    const allTenantResults = await Promise.all(tenantRollupPromises);
    perTenantRollups = allTenantResults.flat();

    devLog(`[getPlatformCrossTenantAnalytics] Found ${perTenantRollups.length} tenant rollup docs across ${tenantIds.length} tenants`);
  } else if (platformDays.length > 0) {
    usedPlatformMetrics = true;
  }

  // ========================================================================
  // 3. Also try to read tenant-day docs from platform_metrics (for per-tenant breakdown)
  // ========================================================================
  let tenantDayDocs = [];
  if (usedPlatformMetrics) {
    try {
      const tenantDaysRef = collection(db, PATHS.col.platformMetricsTenantDays());
      const tenantDaySnapshot = await getDocs(tenantDaysRef);
      tenantDaySnapshot.forEach((docSnap) => {
        const data = docSnap.data();
        const docDay = data.day || '';
        if (docDay >= startDateId && docDay <= endDateId) {
          tenantDayDocs.push({ id: docSnap.id, ...data });
        }
      });
    } catch (err) {
      console.warn('[getPlatformCrossTenantAnalytics] tenant-day docs read failed:', err.message);
    }
  }

  // ========================================================================
  // 4. Aggregate totals from whichever source we have
  // ========================================================================
  const totals = {
    totalRuns: 0, routine: 0, urgent: 0, not_yet: 0,
    completed: 0, overrides: 0, pdfGenerated: 0,
    reclaimedMinutes: 0, estTimeWithoutGateMin: 0,
  };
  let lastUpdatedAt = null;
  let daysWithData = 0;
  const timeSeries = [];
  const byTenant = {};
  const activeTenantIds = new Set();
  const bySpecialty = {};
  const byPack = {};

  if (usedPlatformMetrics) {
    // ---- Aggregate from platform_metrics ----
    platformDays.forEach((day) => {
      const runsTotal = day.runsTotal || 0;
      const notYetCount = day.notYetCount || 0;
      const routineCount = day.routineCount || 0;
      const urgentCount = day.urgentCount || 0;

      totals.totalRuns += runsTotal;
      totals.not_yet += notYetCount;
      totals.routine += routineCount;
      totals.urgent += urgentCount;
      totals.completed += routineCount + urgentCount;
      totals.reclaimedMinutes += day.timeReclaimedMin || 0;
      totals.estTimeWithoutGateMin += day.estTimeWithoutGateMin || 0;
      if (runsTotal > 0) daysWithData++;

      const updatedAt = day.lastUpdatedAt;
      if (updatedAt) {
        const d2 = typeof updatedAt.toDate === 'function' ? updatedAt.toDate() : new Date(updatedAt);
        if (!lastUpdatedAt || d2 > lastUpdatedAt) lastUpdatedAt = d2;
      }

      timeSeries.push({
        dateId: day.day || day.dateId,
        totalRuns: runsTotal, routine: routineCount, urgent: urgentCount,
        not_yet: notYetCount, reclaimedMinutes: day.timeReclaimedMin || 0,
      });
    });

    // Per-tenant from platform_metrics tenant_days
    tenantDayDocs.forEach((td) => {
      const tid = td.tenantId;
      if (!tid) return;
      if (!byTenant[tid]) {
        byTenant[tid] = { tenantId: tid, totalRuns: 0, routine: 0, urgent: 0, not_yet: 0, completed: 0, reclaimedMinutes: 0, estTimeWithoutGateMin: 0 };
      }
      const r = td.runsTotal || 0;
      const ny = td.notYetCount || 0;
      const ro = td.routineCount || 0;
      const ur = td.urgentCount || 0;
      byTenant[tid].totalRuns += r;
      byTenant[tid].not_yet += ny;
      byTenant[tid].routine += ro;
      byTenant[tid].urgent += ur;
      byTenant[tid].completed += ro + ur;
      byTenant[tid].reclaimedMinutes += td.timeReclaimedMin || 0;
      byTenant[tid].estTimeWithoutGateMin += td.estTimeWithoutGateMin || 0;
      if (r > 0) activeTenantIds.add(tid);

      // Extract flat specialty/pack breakdowns
      Object.keys(td).forEach((key) => {
        const specMatch = key.match(/^bySpecialty\.(.+?)\.(runsTotal|notYetCount|routineCount|urgentCount)$/);
        if (specMatch) {
          const [, specId, field] = specMatch;
          if (!bySpecialty[specId]) bySpecialty[specId] = { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0 };
          const val = td[key] || 0;
          if (field === 'runsTotal') bySpecialty[specId].totalRuns += val;
          else if (field === 'notYetCount') bySpecialty[specId].not_yet += val;
          else if (field === 'routineCount') bySpecialty[specId].routine += val;
          else if (field === 'urgentCount') bySpecialty[specId].urgent += val;
        }
        const packMatch = key.match(/^byPathway\.(.+?)\.(runsTotal|notYetCount|routineCount|urgentCount)$/);
        if (packMatch) {
          const [, packId, field] = packMatch;
          if (!byPack[packId]) byPack[packId] = { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0 };
          const val = td[key] || 0;
          if (field === 'runsTotal') byPack[packId].totalRuns += val;
          else if (field === 'notYetCount') byPack[packId].not_yet += val;
          else if (field === 'routineCount') byPack[packId].routine += val;
          else if (field === 'urgentCount') byPack[packId].urgent += val;
        }
      });
    });
  } else {
    // ---- Aggregate from per-tenant rollups (fallback) ----
    // These use flat dotted keys like "counts.totalRuns", "breakdowns.bySpecialty.cardiology.totalRuns"
    const dayTotalsMap = {}; // dateId -> aggregated day totals for timeSeries

    perTenantRollups.forEach((rollup) => {
      const tid = rollup.tenantId;

      if (!byTenant[tid]) {
        byTenant[tid] = { tenantId: tid, totalRuns: 0, routine: 0, urgent: 0, not_yet: 0, completed: 0, reclaimedMinutes: 0, estTimeWithoutGateMin: 0 };
      }

      const dayRuns = getRollupValue(rollup, 'counts.totalRuns');
      const dayRoutine = getRollupValue(rollup, 'counts.routine');
      const dayUrgent = getRollupValue(rollup, 'counts.urgent');
      const dayNotYet = getRollupValue(rollup, 'counts.not_yet');
      const dayCompleted = getRollupValue(rollup, 'counts.completed');
      const dayOverrides = getRollupValue(rollup, 'counts.overrides');
      const dayPdfGenerated = getRollupValue(rollup, 'counts.pdfGenerated');
      const dayReclaimed = getRollupValue(rollup, 'value.reclaimedMinutes');

      // Totals
      totals.totalRuns += dayRuns;
      totals.routine += dayRoutine;
      totals.urgent += dayUrgent;
      totals.not_yet += dayNotYet;
      totals.completed += dayCompleted;
      totals.overrides += dayOverrides;
      totals.pdfGenerated += dayPdfGenerated;
      totals.reclaimedMinutes += dayReclaimed;
      // Compute estTimeWithoutGateMin: not_yet * default minutesSpentPerIncompleteReferral (15)
      const dayEstWithoutGate = dayNotYet * 15;
      totals.estTimeWithoutGateMin += dayEstWithoutGate;

      // Per-tenant
      byTenant[tid].totalRuns += dayRuns;
      byTenant[tid].routine += dayRoutine;
      byTenant[tid].urgent += dayUrgent;
      byTenant[tid].not_yet += dayNotYet;
      byTenant[tid].completed += dayCompleted;
      byTenant[tid].reclaimedMinutes += dayReclaimed;
      byTenant[tid].estTimeWithoutGateMin += dayEstWithoutGate;
      if (dayRuns > 0) activeTenantIds.add(tid);

      // Track updated time
      const updatedAt = getRollupUpdatedAt(rollup);
      if (updatedAt && (!lastUpdatedAt || updatedAt > lastUpdatedAt)) lastUpdatedAt = updatedAt;

      // Time series aggregation by dateId
      const dId = rollup.dateId;
      if (dId) {
        if (!dayTotalsMap[dId]) {
          dayTotalsMap[dId] = { dateId: dId, totalRuns: 0, routine: 0, urgent: 0, not_yet: 0, reclaimedMinutes: 0 };
        }
        dayTotalsMap[dId].totalRuns += dayRuns;
        dayTotalsMap[dId].routine += dayRoutine;
        dayTotalsMap[dId].urgent += dayUrgent;
        dayTotalsMap[dId].not_yet += dayNotYet;
        dayTotalsMap[dId].reclaimedMinutes += dayReclaimed;
      }

      // Specialty breakdown (using existing helpers that handle flat keys)
      const specData = extractSpecialtyBreakdown(rollup);
      Object.entries(specData).forEach(([specId, counts]) => {
        if (!bySpecialty[specId]) bySpecialty[specId] = { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0 };
        bySpecialty[specId].totalRuns += counts.totalRuns || 0;
        bySpecialty[specId].routine += counts.routine || 0;
        bySpecialty[specId].urgent += counts.urgent || 0;
        bySpecialty[specId].not_yet += counts.not_yet || 0;
      });

      // Pack breakdown (using existing helpers that handle flat keys)
      const packData = extractPackBreakdown(rollup);
      Object.entries(packData).forEach(([packId, counts]) => {
        if (!byPack[packId]) byPack[packId] = { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0 };
        byPack[packId].totalRuns += counts.totalRuns || 0;
        byPack[packId].routine += counts.routine || 0;
        byPack[packId].urgent += counts.urgent || 0;
        byPack[packId].not_yet += counts.not_yet || 0;
      });
    });

    // Build timeSeries from aggregated day map
    Object.values(dayTotalsMap).forEach((dt) => {
      timeSeries.push(dt);
      if (dt.totalRuns > 0) daysWithData++;
    });
  }

  // ========================================================================
  // 5. Previous period trend (use tenant rollups fallback too)
  // ========================================================================
  const rangeDays = dateIds.length;
  const prevEndDate2 = new Date(startDate);
  prevEndDate2.setUTCDate(prevEndDate2.getUTCDate() - 1);
  const prevStartDate2 = new Date(prevEndDate2);
  prevStartDate2.setUTCDate(prevStartDate2.getUTCDate() - rangeDays + 1);

  const prevStartDateId = generateDateId(prevStartDate2);
  const prevEndDateId = generateDateId(prevEndDate2);

  const prevDateIds = [];
  for (let d = new Date(prevStartDate2); d <= prevEndDate2; d.setUTCDate(d.getUTCDate() + 1)) {
    prevDateIds.push(generateDateId(d));
  }

  const prevTotals = { totalRuns: 0, reclaimedMinutes: 0, not_yet: 0 };

  if (usedPlatformMetrics) {
    // Read previous from platform_metrics
    const prevDayPromises = prevDateIds.map(async (dateId) => {
      try {
        const snap = await getDoc(doc(db, PATHS.platformMetricsDay(dateId)));
        return snap.exists() ? snap.data() : null;
      } catch { return null; }
    });
    (await Promise.all(prevDayPromises)).filter(Boolean).forEach((day) => {
      prevTotals.totalRuns += day.runsTotal || 0;
      prevTotals.reclaimedMinutes += day.timeReclaimedMin || 0;
      prevTotals.not_yet += day.notYetCount || 0;
    });
  } else if (tenantIds.length > 0) {
    // Read previous from tenant rollups
    const prevTenantPromises = tenantIds.map(async (tenantId) => {
      const dayPromises = prevDateIds.map(async (dateId) => {
        try {
          const snap = await getDoc(doc(db, PATHS.rollupDaily(tenantId, dateId)));
          return snap.exists() ? snap.data() : null;
        } catch { return null; }
      });
      return (await Promise.all(dayPromises)).filter(Boolean);
    });
    (await Promise.all(prevTenantPromises)).flat().forEach((rollup) => {
      prevTotals.totalRuns += getRollupValue(rollup, 'counts.totalRuns');
      prevTotals.reclaimedMinutes += getRollupValue(rollup, 'value.reclaimedMinutes');
      prevTotals.not_yet += getRollupValue(rollup, 'counts.not_yet');
    });
  }

  const totalRunsTrend = prevTotals.totalRuns > 0
    ? Math.round(((totals.totalRuns - prevTotals.totalRuns) / prevTotals.totalRuns) * 100)
    : null;
  const reclaimedTrend = prevTotals.reclaimedMinutes > 0
    ? Math.round(((totals.reclaimedMinutes - prevTotals.reclaimedMinutes) / prevTotals.reclaimedMinutes) * 100)
    : null;

  // ========================================================================
  // 6. Build and return result
  // ========================================================================
  const tenantRankings = Object.values(byTenant)
    .sort((a, b) => b.reclaimedMinutes - a.reclaimedMinutes);

  const hasData = totals.totalRuns > 0;

  return {
    totals,
    byTenant,
    bySpecialty,
    byPack,
    timeSeries: timeSeries.sort((a, b) => (a.dateId || '').localeCompare(b.dateId || '')),
    tenantRankings,
    trends: {
      totalRunsTrend,
      reclaimedTrend,
      prevPeriod: { startDateId: prevStartDateId, endDateId: prevEndDateId },
      prevTotals,
    },
    meta: {
      hasData,
      activeTenants: activeTenantIds.size,
      lastUpdatedAt,
      daysWithData,
      totalDaysInRange: dateIds.length,
      missingDays: dateIds.length - daysWithData,
      source: usedPlatformMetrics ? 'platform_metrics' : 'tenant_rollups',
    },
  };
};

/**
 * Helper to extract value from rollup doc, handling both flat and nested structures
 * Cloud Functions write flat keys like "counts.totalRuns" but we need to handle both formats.
 *
 * @param {Object} rollup - Rollup document
 * @param {string} path - Dot-notation path like "counts.totalRuns"
 * @returns {number} The value or 0 if not found
 */
const getRollupValue = (rollup, path) => {
  // First try flat key (e.g., "counts.totalRuns" as a literal key)
  if (rollup[path] !== undefined) {
    return rollup[path] || 0;
  }

  // Then try nested path (e.g., rollup.counts.totalRuns)
  const parts = path.split('.');
  let value = rollup;
  for (const part of parts) {
    if (value === undefined || value === null) return 0;
    value = value[part];
  }
  return value || 0;
};

/**
 * Extract specialty breakdown from rollup, handling flat key format
 * Flat format: "breakdowns.bySpecialty.cardiology.totalRuns": 1
 *
 * @param {Object} rollup - Rollup document
 * @returns {Object} Map of specialtyId -> { totalRuns, routine, urgent, not_yet }
 */
const extractSpecialtyBreakdown = (rollup) => {
  const result = {};

  // Try nested format first
  if (rollup.breakdowns?.bySpecialty) {
    return rollup.breakdowns.bySpecialty;
  }

  // Handle flat format: "breakdowns.bySpecialty.{specialtyId}.{field}"
  const prefix = 'breakdowns.bySpecialty.';
  Object.keys(rollup).forEach((key) => {
    if (key.startsWith(prefix)) {
      const rest = key.substring(prefix.length);
      const parts = rest.split('.');
      if (parts.length === 2) {
        const [specialtyId, field] = parts;
        if (!result[specialtyId]) {
          result[specialtyId] = { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0 };
        }
        result[specialtyId][field] = rollup[key] || 0;
      }
    }
  });

  return result;
};

/**
 * Extract pack breakdown from rollup, handling flat key format
 * Flat format: "breakdowns.byPack.neurology_headache.totalRuns": 1
 *
 * @param {Object} rollup - Rollup document
 * @returns {Object} Map of packId -> { totalRuns, routine, urgent, not_yet }
 */
const extractPackBreakdown = (rollup) => {
  const result = {};

  // Try nested format first
  if (rollup.breakdowns?.byPack) {
    return rollup.breakdowns.byPack;
  }

  // Handle flat format: "breakdowns.byPack.{packId}.{field}"
  const prefix = 'breakdowns.byPack.';
  Object.keys(rollup).forEach((key) => {
    if (key.startsWith(prefix)) {
      const rest = key.substring(prefix.length);
      const parts = rest.split('.');
      if (parts.length === 2) {
        const [packId, field] = parts;
        if (!result[packId]) {
          result[packId] = { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0 };
        }
        result[packId][field] = rollup[key] || 0;
      }
    }
  });

  return result;
};

/**
 * Helper to extract updatedAt from rollup doc, handling both flat and nested structures
 * Also handles Firestore Timestamp objects
 *
 * @param {Object} rollup - Rollup document
 * @returns {Date|null} The updatedAt date or null if not found
 */
const getRollupUpdatedAt = (rollup) => {
  // Try flat key first
  let updatedAt = rollup.updatedAt || rollup['updatedAt'];

  if (!updatedAt) return null;

  // Handle Firestore Timestamp
  if (updatedAt && typeof updatedAt.toDate === 'function') {
    return updatedAt.toDate();
  }

  // Handle Date object
  if (updatedAt instanceof Date) {
    return updatedAt;
  }

  // Handle ISO string
  if (typeof updatedAt === 'string') {
    return new Date(updatedAt);
  }

  return null;
};

/**
 * Aggregate rollup docs into analytics summary (BLOCK 12)
 *
 * @param {Array} rollups - Array of rollup documents
 * @param {number} totalDaysInRange - Total number of days in the date range (for coverage calculation)
 * @returns {Object} Aggregated analytics
 */
const aggregateRollups = (rollups, totalDaysInRange = 0) => {
  const totals = {
    totalRuns: 0,
    routine: 0,
    urgent: 0,
    not_yet: 0,
    completed: 0,
    overrides: 0,
    pdfGenerated: 0,
    reclaimedMinutes: 0,
  };

  const bySpecialty = {};
  const byPack = {};
  const timeSeries = [];

  // Track metadata for KPI display
  let lastUpdatedAt = null;
  let daysWithData = 0;

  rollups.forEach((rollup) => {
    // Track lastUpdatedAt (max across all rollups)
    const rollupUpdatedAt = getRollupUpdatedAt(rollup);
    if (rollupUpdatedAt && (!lastUpdatedAt || rollupUpdatedAt > lastUpdatedAt)) {
      lastUpdatedAt = rollupUpdatedAt;
    }

    // Count days with actual data
    const dayTotalRuns = getRollupValue(rollup, 'counts.totalRuns');
    if (dayTotalRuns > 0) {
      daysWithData++;
    }

    // Aggregate totals - handle both flat ("counts.totalRuns") and nested (counts: {totalRuns}) structures
    totals.totalRuns += dayTotalRuns;
    totals.routine += getRollupValue(rollup, 'counts.routine');
    totals.urgent += getRollupValue(rollup, 'counts.urgent');
    totals.not_yet += getRollupValue(rollup, 'counts.not_yet');
    totals.completed += getRollupValue(rollup, 'counts.completed');
    totals.overrides += getRollupValue(rollup, 'counts.overrides');
    totals.pdfGenerated += getRollupValue(rollup, 'counts.pdfGenerated');
    totals.reclaimedMinutes += getRollupValue(rollup, 'value.reclaimedMinutes');

    // Aggregate by specialty (handles both flat and nested formats)
    const specialtyData = extractSpecialtyBreakdown(rollup);
    Object.entries(specialtyData).forEach(([specialtyId, counts]) => {
      if (!bySpecialty[specialtyId]) {
        bySpecialty[specialtyId] = { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0 };
      }
      bySpecialty[specialtyId].totalRuns += counts.totalRuns || 0;
      bySpecialty[specialtyId].routine += counts.routine || 0;
      bySpecialty[specialtyId].urgent += counts.urgent || 0;
      bySpecialty[specialtyId].not_yet += counts.not_yet || 0;
    });

    // Aggregate by pack (handles both flat and nested formats)
    const packData = extractPackBreakdown(rollup);
    Object.entries(packData).forEach(([packId, counts]) => {
      if (!byPack[packId]) {
        byPack[packId] = { totalRuns: 0, routine: 0, urgent: 0, not_yet: 0 };
      }
      byPack[packId].totalRuns += counts.totalRuns || 0;
      byPack[packId].routine += counts.routine || 0;
      byPack[packId].urgent += counts.urgent || 0;
      byPack[packId].not_yet += counts.not_yet || 0;
    });

    // Time series
    timeSeries.push({
      dateId: rollup.dateId,
      totalRuns: dayTotalRuns,
      routine: getRollupValue(rollup, 'counts.routine'),
      urgent: getRollupValue(rollup, 'counts.urgent'),
      not_yet: getRollupValue(rollup, 'counts.not_yet'),
      reclaimedMinutes: getRollupValue(rollup, 'value.reclaimedMinutes'),
    });
  });

  // Calculate rates
  const completedRate = totals.totalRuns > 0 ? (totals.completed / totals.totalRuns) * 100 : 0;
  const notYetRate = totals.totalRuns > 0 ? (totals.not_yet / totals.totalRuns) * 100 : 0;
  const urgentRate = totals.totalRuns > 0 ? (totals.urgent / totals.totalRuns) * 100 : 0;
  const overrideRate = totals.totalRuns > 0 ? (totals.overrides / totals.totalRuns) * 100 : 0;

  // Calculate data coverage
  const hasData = rollups.length > 0 && totals.totalRuns > 0;
  const missingDays = totalDaysInRange > 0 ? totalDaysInRange - rollups.length : 0;

  return {
    totals,
    rates: {
      completedRate: Math.round(completedRate),
      notYetRate: Math.round(notYetRate),
      urgentRate: Math.round(urgentRate),
      overrideRate: Math.round(overrideRate),
    },
    value: {
      reclaimedHours: Math.round((totals.reclaimedMinutes / 60) * 10) / 10,
    },
    bySpecialty,
    byPack,
    timeSeries: timeSeries.sort((a, b) => a.dateId.localeCompare(b.dateId)),
    // Metadata for KPI display
    meta: {
      hasData,
      lastUpdatedAt,
      daysWithData,
      totalDaysInRange,
      missingDays,
      rollupsLoaded: rollups.length,
    },
  };
};

// ============================================================================
// BLOCK 14: OPS OBSERVABILITY + FAILURE INBOX
// ============================================================================

/**
 * logOpsError - Log an operational error for platform monitoring (BLOCK 14)
 *
 * Best-effort logging (never throws, never blocks user workflow)
 *
 * FIRESTORE WRITE:
 *   - platform/ops/errors/{errorId} (platform index - single source of truth)
 *
 * NOTE: We only write to the platform index. The tenant-level path
 * tenants/{tenantId}/ops/errors/{errorId} would be invalid (5 segments).
 * All ops errors are stored in the platform-level collection with
 * tenantId stored IN the document for filtering.
 *
 * @param {Object} params - Error parameters
 * @param {string} params.tenantId - Tenant ID (stored in doc, not in path)
 * @param {string} params.category - Error category ('submit_failed'|'permission_denied'|'rollup_failed'|'runtime_error')
 * @param {string} params.message - Safe error message (no PHI, <= 300 chars)
 * @param {string} [params.code] - Firestore error code (e.g., 'permission-denied')
 * @param {Object} params.where - Location context { feature, screen?, action? }
 * @param {Object} params.actor - User context { uid, role, tenantId }
 * @param {Object} [params.context] - Safe context (no PHI) { referralId?, runId?, submissionKey?, eventId?, specialtyId?, packId?, versionId? }
 * @param {string} [params.stackPreview] - Truncated stack trace (<= 500 chars)
 * @param {string} [params.lastFirestorePath] - Last Firestore path attempted
 * @param {string} [params.lastOperation] - Last Firestore operation ('read'|'write'|'query')
 * @param {string} [params.severity] - Severity level ('info'|'warn'|'error') - default 'error'
 * @returns {Promise<void>}
 */
export const logOpsError = async ({
  tenantId,
  category,
  message,
  code = null,
  where,
  actor,
  context = {},
  stackPreview = null,
  lastFirestorePath = null,
  lastOperation = null,
  severity = 'error',
}) => {
  const db = getDb();

  // Validate required params (silent fail on invalid)
  if (!tenantId || !category || !message || !where || !actor) {
    console.warn('[logOpsError] Missing required params, skipping log');
    return;
  }

  // Validate category
  const validCategories = ['submit_failed', 'permission_denied', 'rollup_failed', 'runtime_error'];
  if (!validCategories.includes(category)) {
    console.warn('[logOpsError] Invalid category:', category);
    return;
  }

  try {
    const errorId = generateId();
    const now = toTimestamp();

    // Sanitize message (redact PHI/token-like content, then truncate). Ops errors
    // are a Never-PHI zone; this is best-effort logging so we REDACT (never throw).
    const safeMessage = redactSensitiveForLog(String(message), { maxStringLength: 300 });

    // Sanitize stack preview (redact + truncate)
    const safeStackPreview = stackPreview ? redactSensitiveForLog(String(stackPreview), { maxStringLength: 500 }) : null;

    // Build error doc
    // tenantId is stored IN the document for filtering, not in the path
    const errorDoc = {
      tenantId,
      errorId,
      createdAt: now,
      updatedAt: now,
      severity: severity || 'error',

      category,
      message: safeMessage,
      ...(code ? { code } : {}),

      where: {
        feature: where.feature,
        ...(where.screen ? { screen: where.screen } : {}),
        ...(where.action ? { action: where.action } : {}),
      },

      actor: {
        uid: actor.uid,
        role: actor.role,
        tenantId: actor.tenantId,
      },

      // Safe context only (NO PHI)
      context: {
        ...(context.referralId ? { referralId: context.referralId } : {}),
        ...(context.runId ? { runId: context.runId } : {}),
        ...(context.submissionKey ? { submissionKey: context.submissionKey } : {}),
        ...(context.eventId ? { eventId: context.eventId } : {}),
        ...(context.specialtyId ? { specialtyId: context.specialtyId } : {}),
        ...(context.packId ? { packId: context.packId } : {}),
        ...(context.versionId ? { versionId: context.versionId } : {}),
      },

      ...(safeStackPreview ? { stackPreview: safeStackPreview } : {}),
      ...(lastFirestorePath ? { lastFirestorePath } : {}),
      ...(lastOperation ? { lastOperation } : {}),

      resolved: false,
    };

    // WRITE: Platform-level index doc (single source of truth)
    // Path: platform (collection) -> ops (document) -> errors (subcollection) -> {errorId} (document)
    // This is 4 segments: platform/ops/errors/{errorId} which is valid
    const platformErrorRef = doc(db, PATHS.platformOpsError(errorId));
    await setDoc(platformErrorRef, errorDoc);

    devLog('[logOpsError] ✅ Error logged:', errorId, category);
  } catch (err) {
    // Best-effort: log to console but never throw
    console.error('[logOpsError] ❌ Failed to log error (non-blocking):', err);
  }
};

/**
 * getOpsErrors - Get ops errors for a tenant (BLOCK 14 + Platform Index Optimization)
 *
 * ⚠️ PERFORMANCE UPGRADE:
 * Now queries the platform-level ops error index (platform/ops/errors)
 * instead of expensive collectionGroup scans across all tenant subcollections.
 *
 * Platform admin can view errors across all tenants
 * Tenant admin can view only their tenant's errors
 *
 * FIRESTORE READ:
 *   - platform/ops/errors (query by createdAt desc, filtered by tenantId if specified)
 *
 * @param {string} tenantId - Tenant ID (or null for all tenants - platform admin only)
 * @param {Object} filters - Optional filters { category?, resolved?, daysBack?, limit? }
 * @returns {Promise<Array>} Array of error docs
 */
export const getOpsErrors = async (tenantId, filters = {}) => {
  const db = getDb();
  // Destructure with renamed variable to avoid conflict with Firestore limit function
  const { category = null, resolved = null, daysBack = 7, limit: queryLimitValue = 100 } = filters;

  // Clamp limit to max 500 for safety
  const effectiveLimit = Math.min(Math.max(1, queryLimitValue || 200), 500);

  devLog('[getOpsErrors] Loading errors for tenant:', tenantId || 'all', 'filters:', filters);

  try {
    // Compute date range
    const now = new Date();
    const startDate = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));
    const startTimestamp = Timestamp.fromDate(startDate);

    // ========================================================================
    // PLATFORM INDEX QUERY (replaces collectionGroup scan)
    // Query platform/ops/errors collection for efficient cross-tenant reads
    // ========================================================================

    const platformErrorsRef = collection(db, PATHS.col.platformOpsErrors());

    // Build base query with date range
    let q = query(
      platformErrorsRef,
      where('createdAt', '>=', startTimestamp),
      orderBy('createdAt', 'desc'),
      limit(effectiveLimit)
    );

    // Add tenant filter if specified (platform admin filtering by tenant)
    if (tenantId) {
      q = query(
        platformErrorsRef,
        where('tenantId', '==', tenantId),
        where('createdAt', '>=', startTimestamp),
        orderBy('createdAt', 'desc'),
        limit(effectiveLimit)
      );
    }

    const snapshot = await getDocs(q);

    const errors = [];
    snapshot.forEach((docSnap) => {
      const data = docSnap.data();

      // Apply client-side filters
      if (category && data.category !== category) return;
      if (resolved !== null && data.resolved !== resolved) return;

      errors.push({
        id: docSnap.id,
        ...data,
      });
    });

    devLog('[getOpsErrors] ✅ Found errors:', errors.length, '(from platform index)');
    return errors;
  } catch (err) {
    console.error('[getOpsErrors] ❌ Read failed:', err);
    throw err;
  }
};

/**
 * markOpsErrorResolved - Mark an ops error as resolved with resolution metadata (BLOCK 14)
 *
 * Platform admin only
 *
 * FIRESTORE WRITE:
 *   - platform/ops/errors/{errorId} (platform index - single source of truth)
 *
 * NOTE: We only write to the platform index. The tenant-level path
 * tenants/{tenantId}/ops/errors/{errorId} is invalid (5 segments).
 * All ops errors are stored in the platform-level collection for
 * efficient cross-tenant queries.
 *
 * @param {string} tenantId - Tenant ID (for logging/context, not for path)
 * @param {string} errorId - Error ID
 * @param {string} resolvedByUid - User who resolved the error
 * @param {Object} [resolutionData] - Optional resolution metadata
 * @param {string} [resolutionData.resolutionType] - Resolution type (e.g., 'fixed_by_deploy', 'retried_successfully')
 * @param {string} [resolutionData.resolutionNote] - Optional resolution note
 * @returns {Promise<void>}
 */
export const markOpsErrorResolved = async (tenantId, errorId, resolvedByUid, resolutionData = {}) => {
  const db = getDb();

  if (!errorId || !resolvedByUid) {
    throw new Error('markOpsErrorResolved: errorId and resolvedByUid are required');
  }

  const { resolutionType, resolutionNote } = resolutionData;

  devLog('[markOpsErrorResolved] Resolving error:', errorId, 'tenant:', tenantId || 'unknown', 'type:', resolutionType);

  try {
    // Build update data - only include fields that are provided
    const resolveData = {
      resolved: true,
      resolvedAt: toTimestamp(),
      resolvedByUid,
      updatedAt: toTimestamp(),
    };

    // Add resolution metadata if provided
    if (resolutionType) {
      resolveData.resolutionType = resolutionType;
    }
    if (resolutionNote && resolutionNote.trim()) {
      resolveData.resolutionNote = resolutionNote.trim().substring(0, 1000);
    }

    // UPDATE: Platform-level index doc (single source of truth)
    // Path: platform (collection) -> ops (document) -> errors (subcollection) -> {errorId} (document)
    // This is 4 segments: platform/ops/errors/{errorId} which is valid
    const platformErrorRef = doc(db, PATHS.platformOpsError(errorId));
    await updateDoc(platformErrorRef, resolveData);

    devLog('[markOpsErrorResolved] ✅ Error resolved in platform index');
  } catch (err) {
    console.error('[markOpsErrorResolved] ❌ Update failed:', err);
    throw err;
  }
};

/**
 * undoOpsErrorResolution - Undo a resolved error (set back to unresolved)
 *
 * Platform admin only
 *
 * @param {string} errorId - Error ID
 * @returns {Promise<void>}
 */
export const undoOpsErrorResolution = async (errorId) => {
  const db = getDb();

  if (!errorId) {
    throw new Error('undoOpsErrorResolution: errorId is required');
  }

  devLog('[undoOpsErrorResolution] Undoing resolution for error:', errorId);

  try {
    const undoData = {
      resolved: false,
      resolvedAt: null,
      resolvedByUid: null,
      resolutionType: null,
      resolutionNote: null,
      updatedAt: toTimestamp(),
    };

    const platformErrorRef = doc(db, PATHS.platformOpsError(errorId));
    await updateDoc(platformErrorRef, undoData);

    devLog('[undoOpsErrorResolution] ✅ Resolution undone');
  } catch (err) {
    console.error('[undoOpsErrorResolution] ❌ Undo failed:', err);
    throw err;
  }
};

/**
 * injectOpsTestError - Inject a test error for Ops Dashboard testing (Platform Admin Only)
 *
 * This function allows platform admins to inject test errors into the platform-level
 * ops error collection to verify the Ops Dashboard is working correctly.
 *
 * FIRESTORE WRITE:
 *   - platform/ops/errors/{errorId} (platform index - single source of truth)
 *
 * NOTE: We only write to the platform index. The tenant-level path
 * tenants/{tenantId}/ops/errors/{errorId} would be invalid (5 segments).
 * All ops errors are stored in the platform-level collection for
 * efficient cross-tenant queries.
 *
 * @param {string} tenantId - Tenant ID (REQUIRED - stored in doc for filtering, not in path)
 * @param {Object} options - Optional configuration
 * @param {string} [options.category='runtime_error'] - Error category
 * @param {string} [options.message='TEST: Forced error from platform admin'] - Error message
 * @param {string} [options.injectedByUid] - UID of the admin who injected the error
 * @param {string} [options.injectedByRole] - Role of the admin who injected the error
 * @returns {Promise<string>} The created error ID
 */
export const injectOpsTestError = async (tenantId, options = {}) => {
  const db = getDb();

  if (!tenantId) {
    throw new Error('injectOpsTestError: tenantId is required');
  }

  const {
    category = 'runtime_error',
    message = 'TEST: Forced error from platform admin',
    injectedByUid = 'platform_admin',
    injectedByRole = 'platform_admin',
  } = options;

  // Validate category
  const validCategories = ['submit_failed', 'permission_denied', 'rollup_failed', 'runtime_error'];
  if (!validCategories.includes(category)) {
    throw new Error(`injectOpsTestError: Invalid category '${category}'. Valid: ${validCategories.join(', ')}`);
  }

  devLog('[injectOpsTestError] Injecting test error for tenant:', tenantId, 'category:', category);

  try {
    const errorId = generateId();
    const now = toTimestamp();

    // Build test error doc with enhanced fields for testing
    // tenantId is stored IN the document for filtering, not in the path
    const errorDoc = {
      tenantId,
      errorId,
      createdAt: now,
      updatedAt: now,

      // Enhanced severity support
      severity: 'error',

      category,
      message: String(message).substring(0, 300),

      // Enhanced location info
      location: 'ops_test_injection • inject_test_error',
      route: '/platform/ops',

      where: {
        feature: 'ops_test_injection',
        action: 'inject_test_error',
      },

      // Enhanced actor info with role
      actor: {
        uid: injectedByUid,
        role: injectedByRole,
        tenantId: tenantId,
      },

      // Enhanced context with details
      context: {
        source: 'ops_injector',
      },

      // Enhanced details for debugging
      details: {
        injected: true,
        injectedAt: new Date().toISOString(),
        testPurpose: 'Verify Ops Dashboard functionality',
      },

      // Mark as test error for easy identification
      isTest: true,
      resolved: false,
    };

    // Add category-specific fields for testing
    if (category === 'permission_denied') {
      errorDoc.errorCode = 'permission-denied';
      errorDoc.path = `tenants/${tenantId}/test/permission-check`;
      errorDoc.lastFirestorePath = `tenants/${tenantId}/test/permission-check`;
      errorDoc.lastOperation = 'write';
    } else if (category === 'submit_failed') {
      errorDoc.errorCode = 'failed-precondition';
      errorDoc.entity = {
        referralId: `TEST-REF-${errorId.substring(0, 8)}`,
        runId: `TEST-RUN-${errorId.substring(0, 8)}`,
      };
      errorDoc.lastFirestorePath = `tenants/${tenantId}/referrals/TEST-REF`;
      errorDoc.lastOperation = 'write';
    } else if (category === 'rollup_failed') {
      errorDoc.errorCode = 'internal';
      errorDoc.lastFirestorePath = `tenants/${tenantId}/rollups/daily/2025-01-01`;
      errorDoc.lastOperation = 'write';
    } else {
      // runtime_error - add a sample stack
      errorDoc.stack = `Error: ${message}\n    at injectOpsTestError (store.referrals.js:3700)\n    at handleInjectTestError (OpsView.jsx:105)\n    at onClick (OpsView.jsx:355)`;
    }

    // WRITE: Platform-level index doc (single source of truth)
    // Path: platform (collection) -> ops (document) -> errors (subcollection) -> {errorId} (document)
    // This is 4 segments: platform/ops/errors/{errorId} which is valid
    const platformErrorRef = doc(db, PATHS.platformOpsError(errorId));
    await setDoc(platformErrorRef, errorDoc);

    devLog('[injectOpsTestError] ✅ Test error injected:', errorId);
    return errorId;
  } catch (err) {
    console.error('[injectOpsTestError] ❌ Injection failed:', err);
    throw err;
  }
};

// ============================================================================
// NOT YET INBOX - Query and Rerun Functions
// ============================================================================

/**
 * getNotYetReferrals - Query referrals with outcome.status == "not_yet"
 *
 * FIRESTORE READ:
 *   - tenants/{tenantId}/referrals (where outcome.status == "not_yet")
 *
 * ROLE-BASED FILTERING:
 *   - gp_employee: see only their own submissions (createdByUid filter)
 *   - specialist: see only their specialty's referrals (specialtyId filter)
 *   - tenant_admin: see all not-yet referrals in their tenant
 *   - platform_admin: see all (with tenant selection)
 *
 * @param {Object} params - Query parameters
 * @param {string} params.tenantId - Tenant ID
 * @param {string} params.userId - User ID (for gp_employee filtering)
 * @param {string} params.role - User role
 * @param {string[]} params.specialtyIds - Specialty IDs (for specialist filtering)
 * @param {number} [params.pageSize=10] - Number of items per page
 * @param {DocumentSnapshot|null} [params.lastDoc=null] - Last document from previous page for pagination
 * @returns {Promise<{rows: Array, lastDoc: DocumentSnapshot|null, hasMore: boolean}>} Paginated results
 * @throws {Error} If query fails
 */
export const getNotYetReferrals = async ({ tenantId, userId, role, specialtyIds = [], pageSize = 10, lastDoc = null }) => {
  const db = getDb();

  if (!tenantId || !userId || !role) {
    throw new Error('getNotYetReferrals: tenantId, userId, and role are required');
  }

  devLog('[getNotYetReferrals] Querying not-yet referrals for role:', role, 'pageSize:', pageSize);

  try {
    const referralsRef = collection(db, PATHS.col.referrals(tenantId));

    // Build query constraints based on role
    const constraints = [
      where('outcome.status', '==', 'not_yet'),
    ];

    // Role-based filtering
    if (role === 'gp_employee') {
      // GP employees see only their own submissions
      constraints.push(where('createdByUid', '==', userId));
    } else if (role === 'specialist' || role === 'specialist_admin') {
      // Specialist-side roles see only their specialty's referrals
      if (!specialtyIds || specialtyIds.length === 0) {
        console.warn('[getNotYetReferrals] Specialist has no specialtyIds, returning empty array');
        return { rows: [], lastDoc: null, hasMore: false };
      }

      // Firestore 'in' operator supports max 10 values
      if (specialtyIds.length > 10) {
        console.warn('[getNotYetReferrals] Specialist has >10 specialtyIds, using first 10 only');
        specialtyIds = specialtyIds.slice(0, 10);
      }

      constraints.push(where('specialtyId', 'in', specialtyIds));
    }
    // tenant_admin and platform_admin see all not-yet referrals (no additional filter)

    // Add ordering
    constraints.push(orderBy('savedAt', 'desc'));

    // Add pagination
    if (lastDoc) {
      constraints.push(startAfterDoc(lastDoc));
    }
    constraints.push(limit(pageSize + 1)); // Fetch one extra to check if there are more

    const q = query(referralsRef, ...constraints);
    const snapshot = await getDocs(q);

    const referrals = [];
    snapshot.forEach((docSnap) => {
      referrals.push({
        id: docSnap.id,
        ...docSnap.data(),
      });
    });

    // Determine if there are more results
    const hasMore = referrals.length > pageSize;
    if (hasMore) {
      referrals.pop(); // Remove the extra item
    }

    // Get the last document for next page pagination
    const newLastDoc = snapshot.docs.length > 0 && referrals.length > 0
      ? snapshot.docs[referrals.length - 1]
      : null;

    devLog('[getNotYetReferrals] ✅ Found', referrals.length, 'not-yet referrals, hasMore:', hasMore);
    return { rows: referrals, lastDoc: newLastDoc, hasMore };
  } catch (err) {
    console.error('[getNotYetReferrals] ❌ Query failed:', err);
    throw err;
  }
};

/**
 * getNeedsInfoReferrals — Query ROUTINE/URGENT referrals that intake set to NEEDS_INFO.
 *
 * These are NOT clinical NOT_YET referrals. They are operationally incomplete:
 * intake identified missing documents after the Gate accepted the referral.
 *
 * FIRESTORE READ:
 *   tenants/{tenantId}/referrals
 *     where workflowStatus == 'NEEDS_INFO'
 *     where outcome.status in ['routine', 'urgent']
 *     where awaitingGpDocs == true            (>=1 open document request)
 *     where createdByUid == userId  (gp_employee only)
 *
 * NOTE: workflowStatus and awaitingGpDocs are denormalized query fields. Canonical
 * source is workflow/state (status + rework.missingItemsCount). They are written
 * atomically by the workflow / rework layer. awaitingGpDocs excludes Not-Yet
 * recovery referrals (NEEDS_INFO + recoveryStage READY_FOR_INTAKE_REVIEW, no docs).
 *
 * @param {Object} params
 * @param {string}   params.tenantId
 * @param {string}   params.userId
 * @param {string}   params.role
 * @param {number}   [params.pageSize=20]
 * @param {DocumentSnapshot|null} [params.lastDoc=null]
 * @returns {Promise<{rows: Array, lastDoc: DocumentSnapshot|null, hasMore: boolean}>}
 */
export const getNeedsInfoReferrals = async ({ tenantId, userId, role, pageSize = 20, lastDoc = null }) => {
  const db = getDb();

  if (!tenantId || !userId || !role) {
    throw new Error('getNeedsInfoReferrals: tenantId, userId, and role are required');
  }

  // Specialist-side roles do not use this inbox — they receive via triage/scheduling.
  if (role === 'specialist' || role === 'specialist_admin') {
    return { rows: [], lastDoc: null, hasMore: false };
  }

  devLog('[getNeedsInfoReferrals] Querying for role:', role);

  try {
    const referralsRef = collection(db, PATHS.col.referrals(tenantId));

    const constraints = [
      where('workflowStatus', '==', WORKFLOW_STATUS.NEEDS_INFO),
      where('outcome.status', 'in', ['routine', 'urgent']),
      // Only referrals with >=1 OPEN document request belong in the GP "Docs
      // Requested" lane. This excludes Not-Yet *recovery* referrals (workflow/state
      // recoveryStage = READY_FOR_INTAKE_REVIEW) which sit in NEEDS_INFO awaiting
      // intake review but carry NO document requests — they belong only in the
      // Intake recovery lane, not the GP inbox. awaitingGpDocs is denormalized and
      // maintained by the rework write paths + the attachment-ready Cloud Function.
      where('awaitingGpDocs', '==', true),
    ];

    if (role === 'gp_employee') {
      constraints.push(where('createdByUid', '==', userId));
    }
    // tenant_admin / ops_manager / platform_admin see all — no additional filter.

    constraints.push(orderBy('workflowStatusEnteredAt', 'desc'));
    if (lastDoc) {
      constraints.push(startAfterDoc(lastDoc));
    }
    constraints.push(limit(pageSize + 1));

    const q        = query(referralsRef, ...constraints);
    const snapshot = await getDocs(q);

    const referrals = [];
    snapshot.forEach((d) => referrals.push({ id: d.id, ...d.data() }));

    const hasMore = referrals.length > pageSize;
    if (hasMore) referrals.pop();

    const newLastDoc = snapshot.docs.length > 0 && referrals.length > 0
      ? snapshot.docs[referrals.length - 1]
      : null;

    devLog('[getNeedsInfoReferrals] ✅ Found', referrals.length, 'needs-info referrals, hasMore:', hasMore);
    return { rows: referrals, lastDoc: newLastDoc, hasMore };
  } catch (err) {
    console.error('[getNeedsInfoReferrals] ❌ Query failed:', err);
    throw err;
  }
};

/**
 * rerunNotYetReferral - Re-evaluate a not-yet referral with current pack criteria
 *
 * FIRESTORE READ:
 *   - tenants/{tenantId}/referrals/{referralId}
 *   - tenants/{tenantId}/specialties/{specialtyId}/packs/{packDocId}
 *   - tenants/{tenantId}/metrics (query for max attempt)
 *
 * FIRESTORE WRITE:
 *   - tenants/{tenantId}/referrals/{referralId} (update outcome)
 *   - tenants/{tenantId}/metrics/{eventId} (new metric event for rerun)
 *   - tenants/{tenantId}/referrals/{referralId}/auditLogs/{runId} (audit log)
 *
 * @param {Object} params - Rerun parameters
 * @param {string} params.tenantId - Tenant ID
 * @param {string} params.referralId - Referral ID to rerun
 * @param {string} params.createdByUid - User ID performing the rerun
 * @returns {Promise<Object>} Rerun result with new outcome
 * @throws {Error} If rerun fails
 */
export const rerunNotYetReferral = async ({ tenantId, referralId, createdByUid }) => {
  const db = getDb();

  if (!tenantId || !referralId || !createdByUid) {
    throw new Error('rerunNotYetReferral: tenantId, referralId, and createdByUid are required');
  }

  devLog('[rerunNotYetReferral] Starting rerun for referral:', referralId);

  try {
    // ========================================================================
    // STEP 1: Fetch existing referral document
    // ========================================================================

    const referralRef = doc(db, PATHS.referral(tenantId, referralId));
    const referralSnap = await getDoc(referralRef);

    if (!referralSnap.exists()) {
      throw new Error(`Referral not found: ${referralId}`);
    }

    const referralData = referralSnap.data();

    if (referralData.outcome?.status !== 'not_yet') {
      throw new Error(`Referral ${referralId} is not in NOT_YET status (current: ${referralData.outcome?.status})`);
    }

    devLog('[rerunNotYetReferral] Loaded referral with outcome:', referralData.outcome?.status);

    // ========================================================================
    // STEP 2: Fetch pack to get current criteria
    // ========================================================================

    // Import getPack (will be available from index.js)
    const { getPackByDocId } = await import('./store.firebase.js');

    // Determine packDocId from referral data
    // Firestore pack format: {packId}__{versionId} (double underscore)
    // Backward compatible: newer referrals store packDocId, older ones fallback to computed format
    const packDocId = referralData.packDocId || `${referralData.packId}__${referralData.versionId}`;

    devLog('[rerunNotYetReferral] Looking up pack with docId:', packDocId);

    const pack = await getPackByDocId(tenantId, referralData.specialtyId, packDocId);

    if (!pack) {
      // Fail-fast: block rerun if pack is missing
      throw new Error(`Cannot rerun: Pack missing (${packDocId}). Ask admin to re-upload or activate the pack.`);
    }

    // Verify normalized pack has required fields
    if (!pack.packId || !pack.versionId) {
      console.error('[rerunNotYetReferral] Pack normalization failed:', pack);
      throw new Error(`Cannot rerun: Pack data incomplete (${packDocId}).`);
    }

    devLog('[rerunNotYetReferral] Loaded pack:', pack.packId, 'v' + pack.versionId, 'with', pack.fields?.length || 0, 'fields');

    // ========================================================================
    // STEP 3: Re-run decision engine with stored answers
    // ========================================================================

    // Import decision engine from engine.js
    const { evaluateReferral } = await import('./engine.js');

    const decision = evaluateReferral(
      pack.pack,
      referralData.answers || {},
      referralData.triggeredRedFlagIds || [],
      {
        patient: { localId: referralData.localId }, // Non-PHI only
      }
    );

    // Normalize outcome from engine format ("Urgent"/"Not Yet"/"Routine") to storage format ("urgent"/"not_yet"/"routine")
    const normalizeOutcome = (outcome) => {
      if (!outcome) return undefined;
      if (outcome === 'Urgent') return 'urgent';
      if (outcome === 'Not Yet') return 'not_yet';
      if (outcome === 'Routine') return 'routine';
      // Fallback: lowercase and replace space with underscore
      return outcome.toLowerCase().replace(/ /g, '_');
    };

    const outcomeStatus = normalizeOutcome(decision.outcome);

    devLog('[rerunNotYetReferral] Raw decision outcome:', decision.outcome);
    devLog('[rerunNotYetReferral] Normalized outcomeStatus:', outcomeStatus);

    // FAIL FAST: If outcome is undefined, block the rerun
    if (!outcomeStatus || !['urgent', 'not_yet', 'routine'].includes(outcomeStatus)) {
      console.error('[rerunNotYetReferral] Invalid outcome from engine:', decision.outcome, '→', outcomeStatus);
      throw new Error(`Cannot rerun: Decision engine returned invalid outcome (${decision.outcome}). Pack may be misconfigured.`);
    }

    // ========================================================================
    // STEP 4: Query existing metrics to find max attempt
    // ========================================================================

    const metricsRef = collection(db, PATHS.col.metrics(tenantId));
    const metricsQuery = query(
      metricsRef,
      where('referralId', '==', referralId),
      orderBy('attempt', 'desc'),
      limit(1)
    );

    const metricsSnapshot = await getDocs(metricsQuery);

    let maxAttempt = 0;
    let prevOutcome = referralData.outcome?.status || 'not_yet';

    if (!metricsSnapshot.empty) {
      const latestMetric = metricsSnapshot.docs[0].data();
      maxAttempt = latestMetric.attempt || 0;
      prevOutcome = latestMetric.outcomeStatus || prevOutcome;

      devLog('[rerunNotYetReferral] Found max attempt:', maxAttempt, 'prevOutcome:', prevOutcome);
    } else {
      devLog('[rerunNotYetReferral] No existing metrics found, starting from attempt 1');
    }

    const attempt = maxAttempt + 1;
    const newRunId = `${referralId}-attempt-${attempt}`;

    // ========================================================================
    // STEP 5: Load impact settings (for metrics)
    // ========================================================================

    let impactSettings = null;
    try {
      impactSettings = await getImpactSettings(tenantId);
      devLog('[rerunNotYetReferral] Using impact settings:', impactSettings);
    } catch (err) {
      console.warn('[rerunNotYetReferral] Could not load impact settings, using defaults:', err);
      impactSettings = DEFAULT_IMPACT_SETTINGS;
    }

    // ========================================================================
    // STEP 6: Build NEW MetricEventDoc with incremented attempt
    // ========================================================================

    const metricDoc = buildMetricEvent({
      tenantId,
      specialtyId: referralData.specialtyId,
      packId: referralData.packId,
      versionId: referralData.versionId,
      referralId,
      runId: newRunId,
      createdByUid,
      outcomeStatus: outcomeStatus,  // FIXED: use normalized outcomeStatus
      completenessScore: decision.completenessScore || 0,
      missingFieldIds: decision.missingFieldIds || decision.missingRequiredFieldIds || [],
      triggeredRedFlagIds: decision.triggeredRedFlagIds || [],
      telehealthType: referralData.telehealthContext?.visitType || null,
      telehealthState: referralData.telehealthContext?.stateRegion || null,
      pdfGenerated: false,
      overrideUsed: false,
      attempt,
      prevOutcomeStatus: prevOutcome,
      severitySignals: null,
      impactSettings,
    });

    const metricValidation = validateMetricEvent(metricDoc);
    if (!metricValidation.valid) {
      throw new Error(`Metric event validation failed: ${metricValidation.errors.join(', ')}`);
    }

    const metricRef = doc(db, PATHS.metricEvent(tenantId, metricDoc.eventId));

    devLog('[rerunNotYetReferral] Will write MetricEventDoc with attempt:', attempt);

    // ========================================================================
    // STEP 7: Build NEW AuditLogDoc (with submitter snapshot)
    // ========================================================================

    // Best-effort: read user profile to snapshot name into audit log
    let submitterName = null;
    let submitterEmail = null;
    let submitterRole = null;
    try {
      const { getTenantUser } = await import('./store.firebase.js');
      const userProfile = await getTenantUser(tenantId, createdByUid);
      if (userProfile) {
        submitterName = userProfile.displayName || userProfile.name || null;
        submitterEmail = userProfile.email || null;
        submitterRole = userProfile.role || null;
      }
    } catch {
      // Non-blocking: snapshot is optional
    }

    const auditDoc = buildAuditLog({
      tenantId,
      referralId,
      runId: newRunId,
      specialtyId: referralData.specialtyId,
      packId: referralData.packId,
      versionId: referralData.versionId,
      outcomeStatus: outcomeStatus,  // FIXED: use normalized outcomeStatus
      trace: decision.audit?.trace || decision.trace || [],
      artifacts: decision.nextSteps?.flatMap(s => s.requiredArtifacts || []) || [],
      createdByUid,
      createdByName: submitterName,
      createdByEmail: submitterEmail,
      createdByRole: submitterRole,
      engineVersion: decision.audit?.engineVersion || '2.0.0',
      durationMs: decision.audit?.timestamps?.durationMs || decision.durationMs || 0,
      attempt,
    });

    const auditValidation = validateAuditLog(auditDoc);
    if (!auditValidation.valid) {
      throw new Error(`Audit log validation failed: ${auditValidation.errors.join(', ')}`);
    }

    // Write audit log to nested subcollection (canonical path)
    const auditRef = doc(db, PATHS.referralAuditLog(tenantId, referralId, newRunId));
    // ALSO write to flat audit_logs collection for easy tenant-wide querying
    const flatAuditRef = doc(db, PATHS.auditLogFlat(tenantId, newRunId));

    devLog('[rerunNotYetReferral] Will write AuditLogDoc:', newRunId);

    // ========================================================================
    // STEP 8: Update referral document with new outcome
    // ========================================================================

    const referralUpdate = {
      createdByUid: referralData.createdByUid,  // REQUIRED: Preserve creator (security rule requires this)
      outcome: {
        status: outcomeStatus,  // FIXED: use normalized outcomeStatus
        reason: decision.rationale || decision.reason || '',
      },
      missingFieldIds: decision.missingFieldIds || decision.missingRequiredFieldIds || [],
      triggeredRedFlagIds: decision.triggeredRedFlagIds || [],
      lastEvaluatedAt: toTimestamp(),
      updatedAt: toTimestamp(),
      // Preserve hadNotYet: this referral was in not_yet (that's how it reached here)
      // hadNotYet is durable and never reverts to false
      hadNotYet: true,
      // Mark as resolved from inbox (engine re-run path) — distinguishes from first-run overrides
      resolvedFromInbox: true,
      // Preserve firstNotYetAt if already set, otherwise set it now
      ...(!referralData.firstNotYetAt ? { firstNotYetAt: toTimestamp() } : {}),
    };

    // ========================================================================
    // STEP 9: ATOMIC BATCH WRITE (all succeed or all fail)
    // ========================================================================

    // PERMISSION DEBUG LOGGING
    devLog('[rerunNotYetReferral] BATCH WRITE operations:');
    devLog('[rerunNotYetReferral] 1. UPDATE referral -', `tenants/${tenantId}/referrals/${referralId}`);
    devLog('[rerunNotYetReferral]    Keys:', Object.keys(referralUpdate));
    devLog('[rerunNotYetReferral]    Sample:', referralUpdate);
    devLog('[rerunNotYetReferral] 2. CREATE metric -', `tenants/${tenantId}/metrics/${metricDoc.eventId}`);
    devLog('[rerunNotYetReferral]    Keys:', Object.keys(metricDoc));
    devLog('[rerunNotYetReferral]    Sample:', {
      eventId: metricDoc.eventId,
      createdByUid: metricDoc.createdByUid,
      tenantId: metricDoc.tenantId,
      referralId: metricDoc.referralId,
      runId: metricDoc.runId,
      outcomeStatus: metricDoc.outcomeStatus,
      attempt: metricDoc.attempt,
      prevOutcomeStatus: metricDoc.prevOutcomeStatus,
    });
    devLog('[rerunNotYetReferral] 3. CREATE auditLog -', `tenants/${tenantId}/referrals/${referralId}/auditLogs/${newRunId}`);
    devLog('[rerunNotYetReferral]    Keys:', Object.keys(auditDoc));
    devLog('[rerunNotYetReferral]    Sample:', {
      tenantId: auditDoc.tenantId,
      referralId: auditDoc.referralId,
      runId: auditDoc.runId,
      createdByUid: auditDoc.createdByUid,
      outcomeStatus: auditDoc.outcomeStatus,
      attempt: auditDoc.attempt,
    });

    // DEBUG: Try individual writes to identify which operation fails
    devLog('[rerunNotYetReferral] Attempting UPDATE referral...');
    try {
      await updateDoc(referralRef, referralUpdate);
      devLog('[rerunNotYetReferral] ✅ Referral UPDATE succeeded');
    } catch (err) {
      console.error('[rerunNotYetReferral] ❌ Referral UPDATE failed:', err);
      throw new Error(`Referral UPDATE failed: ${err.message}`);
    }

    devLog('[rerunNotYetReferral] Attempting CREATE metric...');
    try {
      await setDoc(metricRef, metricDoc);
      devLog('[rerunNotYetReferral] ✅ Metric CREATE succeeded');
    } catch (err) {
      console.error('[rerunNotYetReferral] ❌ Metric CREATE failed:', err);
      throw new Error(`Metric CREATE failed: ${err.message}`);
    }

    devLog('[rerunNotYetReferral] Attempting CREATE auditLog (nested)...');
    try {
      await setDoc(auditRef, auditDoc);
      devLog('[rerunNotYetReferral] ✅ AuditLog (nested) CREATE succeeded');
    } catch (err) {
      console.error('[rerunNotYetReferral] ❌ AuditLog (nested) CREATE failed:', err);
      throw new Error(`AuditLog CREATE failed: ${err.message}`);
    }

    devLog('[rerunNotYetReferral] Attempting CREATE auditLog (flat)...');
    try {
      await setDoc(flatAuditRef, auditDoc);
      devLog('[rerunNotYetReferral] ✅ AuditLog (flat) CREATE succeeded');
    } catch (err) {
      console.error('[rerunNotYetReferral] ❌ AuditLog (flat) CREATE failed:', err);
      throw new Error(`AuditLog (flat) CREATE failed: ${err.message}`);
    }

    devLog('[rerunNotYetReferral] ✅ All writes succeeded, attempt:', attempt);

    return {
      referralId,
      runId: newRunId,
      eventId: metricDoc.eventId, // Return eventId so Screen 6 can display it
      attempt,
      newOutcome: outcomeStatus,  // FIXED: use normalized outcomeStatus
      prevOutcome,
    };

  } catch (err) {
    console.error('[rerunNotYetReferral] ❌ Rerun failed:', err);
    throw new Error(`Failed to rerun referral: ${err.message}`);
  }
};

// ============================================================================
// AUDIT LOGS VIEW - Query audit logs with filtering and pagination
// ============================================================================

/**
 * Get audit logs for tenant admin/specialist view with filtering and pagination
 *
 * This queries the legacy audit_logs collection (flat structure):
 * tenants/{tenantId}/audit_logs/{runId}
 *
 * @param {string} tenantId - Tenant ID
 * @param {Object} filters - Filter options
 * @param {string} filters.specialtyId - Filter by specialty (for specialists)
 * @param {string} filters.outcomeStatus - Filter by outcome (urgent/routine/not_yet)
 * @param {Date} filters.startDate - Filter by start date
 * @param {Date} filters.endDate - Filter by end date
 * @param {number} filters.limit - Max results (default 50)
 * @param {Object} filters.startAfterDoc - Firestore doc for pagination
 * @returns {Promise<{logs: Array, lastDoc: Object|null, hasMore: boolean}>}
 */
export const getAuditLogsForView = async (tenantId, filters = {}) => {
  const db = getDb();
  const {
    specialtyId = null,
    specialtyIds = null,  // Support array of specialties for specialists with multiple specialties
    outcomeStatus = null,
    startDate = null,
    endDate = null,
    limit: limitCount = 50,
    startAfterDoc: paginationCursor = null,
  } = filters;

  devLog('[getAuditLogsForView] Loading audit logs for tenant:', tenantId, 'filters:', filters);

  try {
    const logsRef = collection(db, PATHS.col.auditLogsFlat(tenantId));

    // DEBUG: First try without orderBy to test if it's an index issue
    devLog('[getAuditLogsForView] Checking collection path:', `tenants/${tenantId}/audit_logs`);

    // Build query constraints
    const constraints = [];

    // Specialty filter (for specialists)
    // Support both single specialtyId and array of specialtyIds
    if (specialtyIds && specialtyIds.length > 0) {
      // Use 'in' operator for multiple specialties (max 30 values in Firestore)
      constraints.push(where('specialtyId', 'in', specialtyIds.slice(0, 30)));
    } else if (specialtyId) {
      constraints.push(where('specialtyId', '==', specialtyId));
    }

    // Outcome filter
    if (outcomeStatus) {
      constraints.push(where('outcomeStatus', '==', outcomeStatus));
    }

    // Date range filter
    if (startDate) {
      constraints.push(where('createdAt', '>=', Timestamp.fromDate(startDate)));
    }
    if (endDate) {
      // Add 1 day to include the entire end date
      const endOfDay = new Date(endDate);
      endOfDay.setDate(endOfDay.getDate() + 1);
      constraints.push(where('createdAt', '<', Timestamp.fromDate(endOfDay)));
    }

    // Order by creation date
    constraints.push(orderBy('createdAt', 'desc'));

    // Pagination
    if (paginationCursor) {
      constraints.push(startAfterDoc(paginationCursor));
    }

    // Limit (fetch one extra to check if there's more)
    constraints.push(limit(limitCount + 1));

    devLog('[getAuditLogsForView] Query constraints:', constraints.length);

    const q = query(logsRef, ...constraints);
    const snapshot = await getDocs(q);

    devLog('[getAuditLogsForView] Snapshot size:', snapshot.size, 'empty:', snapshot.empty);

    const logs = [];
    let lastVisible = null;
    let count = 0;

    // Note: Firestore forEach doesn't provide index as second parameter!
    // Use a counter instead
    snapshot.forEach((docSnap) => {
      if (count < limitCount) {
        const data = docSnap.data();
        logs.push({
          id: docSnap.id,
          ...data,
        });
        lastVisible = docSnap;
      }
      count++;
    });

    const hasMore = snapshot.size > limitCount;

    devLog('[getAuditLogsForView] ✅ Found logs:', logs.length, 'hasMore:', hasMore);

    return {
      logs,
      lastDoc: lastVisible,
      hasMore,
    };
  } catch (err) {
    console.error('[getAuditLogsForView] ❌ Read failed:', err);
    throw err;
  }
};

/**
 * Get a single audit log by runId for detail view
 *
 * @param {string} tenantId
 * @param {string} runId
 * @returns {Promise<Object|null>}
 */
export const getAuditLogDetail = async (tenantId, runId) => {
  const db = getDb();

  devLog('[getAuditLogDetail] Loading audit log:', runId);

  try {
    const logRef = doc(db, PATHS.auditLogFlat(tenantId, runId));
    const snapshot = await getDoc(logRef);

    if (!snapshot.exists()) {
      devLog('[getAuditLogDetail] Log not found');
      return null;
    }

    return {
      id: snapshot.id,
      ...snapshot.data(),
    };
  } catch (err) {
    console.error('[getAuditLogDetail] ❌ Read failed:', err);
    throw err;
  }
};

/**
 * Migrate existing audit logs from nested structure to flat collection
 *
 * This is a one-time migration to copy audit logs from:
 *   tenants/{tenantId}/referrals/{referralId}/auditLogs/{runId}
 * to:
 *   tenants/{tenantId}/audit_logs/{runId}
 *
 * @param {string} tenantId - Tenant ID to migrate
 * @returns {Promise<{migrated: number, errors: number}>}
 */
export const migrateAuditLogsToFlatCollection = async (tenantId) => {
  const db = getDb();
  devLog('[migrateAuditLogs] Starting migration for tenant:', tenantId);
  devLog('[migrateAuditLogs] Target path:', `tenants/${tenantId}/audit_logs`);

  let migrated = 0;
  let errors = 0;

  try {
    // Step 1: Get all referrals for this tenant
    const referralsRef = collection(db, PATHS.col.referrals(tenantId));
    const referralsSnapshot = await getDocs(referralsRef);

    devLog('[migrateAuditLogs] Found referrals:', referralsSnapshot.size);

    // Step 2: For each referral, get its audit logs
    for (const referralDoc of referralsSnapshot.docs) {
      const referralId = referralDoc.id;

      try {
        const auditLogsRef = collection(db, PATHS.col.referralAuditLogs(tenantId, referralId));
        const auditLogsSnapshot = await getDocs(auditLogsRef);

        devLog(`[migrateAuditLogs] Referral ${referralId}: found ${auditLogsSnapshot.size} audit logs`);

        // Step 3: Copy each audit log to flat collection (FORCE overwrite)
        for (const auditDoc of auditLogsSnapshot.docs) {
          const auditData = auditDoc.data();
          const runId = auditDoc.id;

          try {
            // FORCE write - always write, don't check if exists
            const flatRef = doc(db, PATHS.auditLogFlat(tenantId, runId));
            devLog(`[migrateAuditLogs] FORCE writing to: tenants/${tenantId}/audit_logs/${runId}`);
            devLog(`[migrateAuditLogs] Data sample:`, { runId: auditData.runId, tenantId: auditData.tenantId, outcomeStatus: auditData.outcomeStatus });

            await setDoc(flatRef, auditData);
            migrated++;
            devLog(`[migrateAuditLogs] ✅ Migrated: ${runId}`);
          } catch (err) {
            console.error(`[migrateAuditLogs] ❌ Failed to migrate ${runId}:`, err.code, err.message);
            errors++;
          }
        }
      } catch (err) {
        console.error(`[migrateAuditLogs] ❌ Failed to read audit logs for referral ${referralId}:`, err);
        errors++;
      }
    }

    devLog(`[migrateAuditLogs] ✅ Migration complete: ${migrated} migrated, ${errors} errors`);
    return { migrated, errors };
  } catch (err) {
    console.error('[migrateAuditLogs] ❌ Migration failed:', err);
    throw err;
  }
};

// ============================================================================
// UPDATE NOT-YET REFERRAL ANSWERS (for completing from Not-Yet Inbox)
// ============================================================================

export const updateNotYetReferralAnswers = async ({ tenantId, referralId, answers, triggeredRedFlagIds, age, sex }) => {
  if (!tenantId) throw new Error('updateNotYetReferralAnswers: tenantId required');
  if (!referralId) throw new Error('updateNotYetReferralAnswers: referralId required');
  if (!answers) throw new Error('updateNotYetReferralAnswers: answers required');

  devLog('[updateNotYetReferralAnswers] Updating referral:', referralId);

  try {
    const referralRef = doc(db, PATHS.referral(tenantId, referralId));

    // Update answers, triggered red flags, and patient demographics (non-PHI)
    const updateData = {
      answers,
      triggeredRedFlagIds: triggeredRedFlagIds || [],
      updatedAt: toTimestamp(),
    };
    // Persist age/sex if provided (non-PHI demographic categories)
    if (age) updateData.age = age;
    if (sex) updateData.sex = sex;

    await updateDoc(referralRef, updateData);

    devLog('[updateNotYetReferralAnswers] ✅ Answers updated');

    return { referralId, updated: true };
  } catch (err) {
    console.error('[updateNotYetReferralAnswers] ❌ Update failed:', err);
    throw new Error(`Failed to update referral answers: ${err.message}`);
  }
};

// ============================================================================
// OVERRIDE NOT-YET REFERRAL (from Not-Yet Inbox, no run session required)
// Directly updates referral doc with override + creates metric event
// ============================================================================

export const overrideNotYetReferral = async ({
  tenantId,
  referralId,
  overrideReason,
  overrideNote,
  confirmedByUid,
  confirmedByRole,
}) => {
  const db = getDb();

  if (!tenantId) throw new Error('overrideNotYetReferral: tenantId required');
  if (!referralId) throw new Error('overrideNotYetReferral: referralId required');
  if (!overrideReason) throw new Error('overrideNotYetReferral: overrideReason required');
  if (!confirmedByUid) throw new Error('overrideNotYetReferral: confirmedByUid required');
  if (!confirmedByRole) throw new Error('overrideNotYetReferral: confirmedByRole required');

  devLog('[overrideNotYetReferral] Overriding referral:', referralId, 'reason:', overrideReason);

  try {
    // ========================================================================
    // STEP 1: Read existing referral
    // ========================================================================
    const referralRef = doc(db, PATHS.referral(tenantId, referralId));
    const referralSnap = await getDoc(referralRef);

    if (!referralSnap.exists()) {
      throw new Error('Referral not found');
    }

    const referralData = referralSnap.data();

    // Verify referral is currently not_yet
    if (referralData.outcome?.status !== 'not_yet') {
      throw new Error(`Referral is not in not_yet status (current: ${referralData.outcome?.status})`);
    }

    devLog('[overrideNotYetReferral] Referral found, current status:', referralData.outcome?.status);

    // ========================================================================
    // STEP 2: Determine final outcome (override always resolves to routine)
    // ========================================================================
    const hasRedFlags = (referralData.triggeredRedFlagIds || []).length > 0;
    const finalStatus = hasRedFlags ? 'urgent' : 'routine';

    devLog('[overrideNotYetReferral] hasRedFlags:', hasRedFlags, 'finalStatus:', finalStatus);

    // ========================================================================
    // STEP 3: Query existing metrics to find max attempt
    // ========================================================================
    const metricsRef = collection(db, PATHS.col.metrics(tenantId));
    const metricsQuery = query(
      metricsRef,
      where('referralId', '==', referralId),
      orderBy('attempt', 'desc'),
      limit(1)
    );

    const metricsSnapshot = await getDocs(metricsQuery);
    let maxAttempt = 0;
    let prevOutcome = referralData.outcome?.status || 'not_yet';

    if (!metricsSnapshot.empty) {
      const latestMetric = metricsSnapshot.docs[0].data();
      maxAttempt = latestMetric.attempt || 0;
      prevOutcome = latestMetric.outcomeStatus || prevOutcome;
    }

    const attempt = maxAttempt + 1;
    const newRunId = `${referralId}-override-${attempt}`;

    devLog('[overrideNotYetReferral] attempt:', attempt, 'prevOutcome:', prevOutcome);

    // ========================================================================
    // STEP 4: Load impact settings
    // ========================================================================
    let impactSettings = null;
    try {
      impactSettings = await getImpactSettings(tenantId);
    } catch (err) {
      console.warn('[overrideNotYetReferral] Could not load impact settings, using defaults:', err);
      impactSettings = DEFAULT_IMPACT_SETTINGS;
    }

    // ========================================================================
    // STEP 5: Build metric event for the override
    // ========================================================================
    const metricDoc = buildMetricEvent({
      tenantId,
      specialtyId: referralData.specialtyId,
      packId: referralData.packId,
      versionId: referralData.versionId,
      referralId,
      runId: newRunId,
      createdByUid: confirmedByUid,
      outcomeStatus: finalStatus,
      completenessScore: 0,
      missingFieldIds: referralData.missingFieldIds || [],
      triggeredRedFlagIds: referralData.triggeredRedFlagIds || [],
      telehealthType: referralData.telehealthContext?.visitType || null,
      telehealthState: referralData.telehealthContext?.stateRegion || null,
      pdfGenerated: false,
      overrideUsed: true,
      overrideReasonCode: overrideReason,
      attempt,
      prevOutcomeStatus: prevOutcome,
      severitySignals: null,
      impactSettings,
    });

    const metricRef = doc(db, PATHS.metricEvent(tenantId, metricDoc.eventId));

    // ========================================================================
    // STEP 6: Build audit log for the override
    // ========================================================================

    // Best-effort: read user profile for snapshot
    let submitterName = null;
    let submitterEmail = null;
    try {
      const { getTenantUser } = await import('./store.firebase.js');
      const userProfile = await getTenantUser(tenantId, confirmedByUid);
      if (userProfile) {
        submitterName = userProfile.displayName || userProfile.name || null;
        submitterEmail = userProfile.email || null;
      }
    } catch {
      // Non-blocking
    }

    const auditDoc = buildAuditLog({
      tenantId,
      referralId,
      runId: newRunId,
      specialtyId: referralData.specialtyId,
      packId: referralData.packId,
      versionId: referralData.versionId,
      outcomeStatus: finalStatus,
      trace: [{
        stepId: 'override',
        kind: 'override',
        result: 'triggered',
        detail: `Not-Yet inbox override: ${overrideReason}`,
        t: toTimestamp(),
      }],
      artifacts: [],
      createdByUid: confirmedByUid,
      createdByName: submitterName,
      createdByEmail: submitterEmail,
      createdByRole: confirmedByRole,
      engineVersion: 'override-inbox',
      durationMs: 0,
      attempt,
    });

    const auditRef = doc(db, PATHS.referralAuditLog(tenantId, referralId, newRunId));
    const flatAuditRef = doc(db, PATHS.auditLogFlat(tenantId, newRunId));

    // ========================================================================
    // STEP 7: Atomic write — update referral + create metric + create audit
    // ========================================================================

    const now = toTimestamp();

    const referralUpdate = {
      createdByUid: referralData.createdByUid, // Preserve creator (security rule)
      outcome: {
        status: finalStatus,
        reason: `Override from Not-Yet Inbox: ${overrideReason}`,
        isOverride: true,
        overrideByUid: confirmedByUid,
        overrideAt: now,
        overrideReason: overrideReason,
        overrideNote: overrideNote || null,
      },
      override: {
        used: true,
        reason: overrideReason,
        note: overrideNote || null,
        confirmedByUid,
        confirmedByRole,
        confirmedAt: now,
      },
      // hadNotYet is durable — always true since this was a not_yet referral
      hadNotYet: true,
      // Mark as resolved from inbox (override path) — distinguishes from first-run overrides
      resolvedFromInbox: true,
      ...(!referralData.firstNotYetAt ? { firstNotYetAt: now } : {}),
      updatedAt: now,
      lastEvaluatedAt: now,
    };

    devLog('[overrideNotYetReferral] Writing referral update + metric + audit...');

    // Write referral update
    try {
      await updateDoc(referralRef, referralUpdate);
      devLog('[overrideNotYetReferral] ✅ Referral UPDATE succeeded');
    } catch (err) {
      console.error('[overrideNotYetReferral] ❌ Referral UPDATE failed:', err);
      throw new Error(`Referral UPDATE failed: ${err.message}`);
    }

    // Write metric event
    try {
      await setDoc(metricRef, metricDoc);
      devLog('[overrideNotYetReferral] ✅ Metric CREATE succeeded');
    } catch (err) {
      console.error('[overrideNotYetReferral] ❌ Metric CREATE failed:', err);
      throw new Error(`Metric CREATE failed: ${err.message}`);
    }

    // Write audit logs (best-effort for flat copy)
    try {
      await setDoc(auditRef, auditDoc);
      devLog('[overrideNotYetReferral] ✅ AuditLog CREATE succeeded (nested)');
    } catch (err) {
      console.error('[overrideNotYetReferral] ❌ AuditLog CREATE failed (nested):', err);
      throw new Error(`AuditLog CREATE failed: ${err.message}`);
    }

    try {
      await setDoc(flatAuditRef, auditDoc);
      devLog('[overrideNotYetReferral] ✅ AuditLog CREATE succeeded (flat)');
    } catch (err) {
      console.warn('[overrideNotYetReferral] ⚠ Flat audit log write failed (non-blocking):', err);
    }

    devLog('[overrideNotYetReferral] ✅ Override complete:', referralId, '→', finalStatus);

    return {
      success: true,
      referralId,
      runId: newRunId,
      finalStatus,
      attempt,
      eventId: metricDoc.eventId,
    };
  } catch (err) {
    console.error('[overrideNotYetReferral] ❌ Override failed:', err);
    throw err;
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  // Referral operations
  saveReferral,
  getReferral,
  getReferralsByUser,
  getReferralsBySpecialty,

  // Metric operations
  saveMetricEventV2,
  getMetricsForAnalytics,
  getMetricsByUserV2,
  getMetricsBySpecialtyV2,

  // Audit log operations
  saveAuditLogV2,
  getAuditLog,
  getAuditLogsForReferral,

  // Atomic submission
  submitReferral,

  // BLOCK 2: Re-run gate (persistence)
  rerunReferralGate,

  // BLOCK 3: PDF generated tracking
  markPdfGenerated,

  // BLOCK 4: Impact settings
  getImpactSettings,
  saveImpactSettings,

  // BLOCK 13: PHI policy settings
  getPhiPolicy,
  savePhiPolicy,

  // BLOCK 13: PHI warning logging (non-PHI)
  logPhiWarning,
  getPhiWarnings,

  // BLOCK 13: PHI warning dismissal + audit trail
  dismissPhiWarning,
  isPhiWarningDismissed,
  getPhiDismissalsForRun,

  // Analytics
  getOutcomeCounts,
  getOutcomeRates,

  // BLOCK 5: Conversion tracking
  getConversionMetrics,
  getMetricsByReferralId,

  // BLOCK 6: Tenant analytics dashboard
  getTenantAnalytics,

  // BLOCK 10: Run session telemetry
  startRunSession,
  updateRunSessionProgress,
  markRunSessionSubmitted,
  markRunSessionAbandoned,
  getRunSessionFunnelMetrics,

  // Decision Override
  saveOverrideToRunSession,
  getOverrideFromRunSession,

  // BLOCK 12: Daily rollup backfill and analytics
  backfillDailyRollups,
  getPlatformAnalyticsFromRollups,

  // Platform cross-tenant analytics (reads platform_metrics rollups)
  getPlatformCrossTenantAnalytics,

  // BLOCK 14: Ops observability + failure inbox
  logOpsError,
  getOpsErrors,
  markOpsErrorResolved,

  // NOT YET INBOX: Query and rerun
  getNotYetReferrals,
  rerunNotYetReferral,
  updateNotYetReferralAnswers,
  overrideNotYetReferral,

  // AUDIT LOGS VIEW: Query and detail
  getAuditLogsForView,
  getAuditLogDetail,

  // MIGRATION: One-time migration of audit logs to flat collection
  migrateAuditLogsToFlatCollection,
};
